import { findAddressMatches } from "./duplicateAddressCheck";
import { selectPendingFileRequests } from "./fileRequestQueue";
import { shareRead } from "./sharedRead";
// Every deal save calls retireLaunchRead("deals") just before it writes, so no screen
// opened after a save is handed the sign-in copy of every deal (utils/launchData.js;
// pinned by utils/__tests__/dealWritesRetireLaunchCopy.test.js).
import { claimLaunchRead, retireLaunchRead, startLaunchData } from "./launchData";
import { forgetContacts } from "./contactCache";
import { withoutArchived } from "./contactArchive";
import { collection, doc, getDocs, query, orderBy, arrayUnion, getDoc, where, onSnapshot, limit, startAfter, getCountFromServer, documentId, serverTimestamp, increment } from "firebase/firestore";
import { firestored, storage } from "../firebase/firebaseConfig";
import { deleteContractHistory } from "./contractHistoryStore";
import { authedFetch } from "./authedFetch";
import { getDealsToAutoArchive } from "./autoArchive";
import { doorCodeOf, hasDoorCode } from "./sellerDoorCode";
import { refreshUpcomingOpenHouseDoorCodes, resolveLockboxSnapshots } from "./sellerDoorCodeStore";
import { dealLockboxes } from "./lockboxes";
import { normalizePro, isLoggablePro } from "./teamDirectory";
import { listClients } from "./leadClients";
import { mergeClientIds } from "./intakeLead";
import { planStatusCleanup } from "./statusSheetData";
import {
  auditDealsForGuessedClosingDates,
  looksLikeGuessedClosingDate,
} from "./closingDateAudit";
import { planDuplicateMerge, computeMerge } from "./duplicateLeads";
import { ensureLeadOwner } from "./leadOwnership";
import { canSearchTeamContacts } from "./roles";
import { tagTeamContacts } from "./teamContacts";
import {
  buildUpForGrabsPostText,
  buildUpForGrabsFilledText,
  buildUpForGrabsCancelledText,
} from "./upForGrabsFeed";
import { findClashes, clashWhen } from "./openHouseClash";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { ref, getDownloadURL } from "firebase/storage";
import {
  computeNewReactionState,
  applyReactionDeltas,
  EMPTY_REACTION_COUNTS,
} from "./feedHelpers";
import { alertTeamAboutPostInBackground } from "./feedPostAlerts";
import { generateFeedbackToken } from "./openHouseHelpers";
import { decisionTokenExpiresAt, todayIso, validateRequestWindow } from "./openHouseRequests";
import { findAgentCoordinatorTasks, findAgentClientCareTasks, movesWithLC, movesWithClientCare, buildDealBackfill, ACTIVE_STAGES, STAGE_ORDER, templateForSide } from "./taskSections";
import { stageEntryPatch, todayYMD as prepTodayYMD, DUE_ANCHORS, recomputeAnchoredDeadlines, startListingPrepPatch, cleanDealTask, coordinatorsFor, isOpenTask } from "./listingPrep";
import { jobOwnerChanges } from "./workJobs";
import { applyFileRequestDetails } from "./fileRequestDetails";
import { findAppointmentForLead } from "./appointmentMatch";
import { buildDealFromLead } from "./buildDealFromLead";
import { withDefaultAdminFee } from "./adminFee";
import { carryNetSheetToDeal } from "./netSheet";
import { loadTeamDefaults } from "./teamDefaults";
import { clientCareStartTasks } from "./clientCareChecklistStart";
import { buildBuyerNeedPost } from "./buyerNeedPost";
import {
  upsertRequest,
  applyWinner,
  declineAll,
} from "./openHouseOpportunityHelpers";
import { replaceActivity, removeActivity, lastContactedAfterDelete, activityDateIso } from "./buyerActivity";
import { updateDoc, setDoc, deleteDoc, runTransaction, writeBatch, uploadBytesResumable, uploadBytes, deleteObject } from "./lookOnlyFirestore";
import { assertCanWrite, isLookOnly, lookOnlyRejection } from "./lookOnly";
import { saveWhenConnected } from "./saveWhenConnected";

const auth = getAuth();

export const saveuserdata = async (data, uid) => {
  assertCanWrite();
  return await setDoc(doc(firestored, "users", `${uid}`), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const userlogin = async (email, password) => {
  return await signInWithEmailAndPassword(auth, email, password)
    .then((userCredential) => {
      // Signed in
      const user = userCredential.user;
      return user;
      // ...
    })
    .catch((err) => {
      return err;
    });
};

// What an archive writes to a deal. Seller Provided Door Code (Henry, 2026-09-18): archiving a
// CANCELLED listing also clears its door code, in the same write, so a dead listing never keeps the
// seller's code on file. Only when it holds one, so every other archive writes exactly what it did
// before. Nothing else clears it: another stage, a restore and Contract Fell Through all leave it.
// "Cancelled" in any case, the way autoArchive.js matches it.
const archivePatchFor = (deal, archived) => {
  const patch = { archived: !!archived };
  const cancelled = String(deal?.dealStage).toLowerCase() === "cancelled";
  if (patch.archived && cancelled && hasDoorCode(deal)) patch.sellerDoorCode = "";
  return patch;
};

// The archive write itself. getAlldealforAdmin's automatic archive of old cancelled
// deals calls it directly: every screen applies that same archive (door code clearing
// included) to its own copy of the deals, so the sign-in copy stays true and the next
// screen can still reuse it.
const writeDealArchived = async (dealId, patch) => {
  const docRef = doc(firestored, "deals", String(dealId));
  // launch-copy-safe: see above. Every other archive goes through setDealArchived.
  await setDoc(docRef, patch, { merge: true });
  // The door code was just cleared: clear it on the listing's upcoming open houses too, the
  // same way a Clear on the listing page does (spec rule 2). Cancelling a listing does not
  // cancel its open houses, so a Scheduled one could otherwise be re-sent the old code.
  // Fire-and-forget: a failure is logged and never fails the archive.
  if (patch.sellerDoorCode === "") {
    refreshUpcomingOpenHouseDoorCodes(String(dealId), "", todayIso()).catch((e) =>
      console.error("Open house door code refresh failed for deal", dealId, e),
    );
  }
  return true;
};

// Soft-archive a deal: hidden from the board by default; restore by setting false.
// Archiving reads the deal fresh first: the Deals Board passes only the id, and the
// door code rule above needs the deal's current stage and code.
export const setDealArchived = async (dealId, archived) => {
  const deal = archived
    ? (await getDoc(doc(firestored, "deals", String(dealId)))).data()
    : null;
  retireLaunchRead("deals");
  return writeDealArchived(dealId, archivePatchFor(deal, archived));
};

// Soft delete: mark the deal as deleted with a timestamp. Kept for 30 days
// in the Recently Deleted view, then can be permanently purged. Photos are
// preserved so a restore brings the deal back intact.
export const deleteDealWithPhotos = async (dealId) => {
  assertCanWrite();
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    retireLaunchRead("deals");
    await setDoc(docRef, { deletedAt: Date.now() }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error soft-deleting deal:", error);
    return false;
  }
};

// Clear the deletedAt flag — brings a soft-deleted deal back into the main list.
export const restoreDeal = async (dealId) => {
  assertCanWrite();
  try {
    const docRef = doc(firestored, "deals", String(dealId));
    // Firestore can't unset via setDoc merge with undefined — write null and
    // treat null/0/missing as "not deleted" in the read filters.
    retireLaunchRead("deals");
    await setDoc(docRef, { deletedAt: null }, { merge: true });
    return true;
  } catch (error) {
    console.error("🚨 Error restoring deal:", error);
    return false;
  }
};

// Permanent destroy — used by the Recently Deleted view when the user clicks
// "Delete forever" or by the 30-day auto-purge. This is the original
// destructive behavior.
export const permanentlyDeleteDeal = async (dealId) => {
  assertCanWrite();
  try {
    const dealsCollection = collection(firestored, "deals");
    const q = query(dealsCollection, where("createdat", "==", dealId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) return false;

    for (const dealDoc of querySnapshot.docs) {
      // Saved Previous Contracts live in deals/{id}/contractHistory, and deleting
      // a deal document never deletes its subcollections. Clear them FIRST: if
      // that fails, the catch below returns false before any photo or the deal
      // is touched, so a retry finds everything still in place.
      await deleteContractHistory(dealDoc.id);
      const dealData = dealDoc.data();
      if (Array.isArray(dealData.propertyphoto)) {
        for (const photoUrl of dealData.propertyphoto) {
          try {
            await deleteObject(ref(storage, photoUrl));
          } catch (imgErr) {
            console.warn("⚠️ Image delete failed:", photoUrl, imgErr);
          }
        }
      }
      retireLaunchRead("deals");
      await deleteDoc(doc(firestored, "deals", dealDoc.id));
    }
    return true;
  } catch (error) {
    console.error("🚨 Error permanently deleting deal:", error);
    return false;
  }
};

// Return only deals that have been soft-deleted (deletedAt is a number > 0).
export const getDeletedDeals = async () => {
  const items = await getDocs(collection(firestored, "deals"));
  return items.docs
    .map((d) => d.data())
    .filter((d) => typeof d.deletedAt === "number" && d.deletedAt > 0)
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
};

export const updateUserProfile = async (userId, updatedData) => {
  assertCanWrite();
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

// ---- Follow Up Boss integration (single team-wide connection) --------------
// Stored in ONE doc (integrations/followUpBoss) so the backend (functions-fub)
// reads it without scanning users. The owner connects once with the owner API
// key, which covers the whole shared FUB account.
export const getFubIntegration = async () => {
  try {
    const snap = await getDoc(doc(firestored, "integrations", "followUpBoss"));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("Error reading Follow Up Boss integration:", error);
    return null;
  }
};

export const saveFubIntegration = async (data) => {
  assertCanWrite();
  try {
    await setDoc(doc(firestored, "integrations", "followUpBoss"), data, {
      merge: true,
    });
    return true;
  } catch (error) {
    console.error("Error saving Follow Up Boss integration:", error);
    return false;
  }
};

// Base URL for the Follow Up Boss sync Cloud Functions (isolated "fub" codebase).
const FUB_FUNCTIONS_BASE =
  "https://us-central1-team-edelstein.cloudfunctions.net";

// Fire-and-forget: push a lead to Follow Up Boss after it's saved/changed. The
// endpoint no-ops when sync is disconnected, so it's always safe to call, and a
// failure here never breaks the save.
export const pushLeadToFub = (leadId, type) => {
  if (!leadId) return;
  try {
    authedFetch(`${FUB_FUNCTIONS_BASE}/fubPush`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ leadId, type }),
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
};

// One-time "Activate Live Sync": asks the backend to register the FUB webhooks.
export const registerFubWebhooks = async () => {
  assertCanWrite();
  try {
    const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubRegisterWebhooks`, {
      method: "POST",
    });
    return await res.json().catch(() => ({ ok: false }));
  } catch (e) {
    return { ok: false, error: String(e) };
  }
};

// Pick-and-choose import: list ALL Follow Up Boss contacts for preview (no tag
// gate, no writes), then import only the selected ones (de-duped on the
// backend via fub_<id>). Both throw on failure so the modal can surface it.
export const listFubContacts = async () => {
  const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubListContacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Could not load Follow Up Boss contacts");
  }
  return res.json(); // { ok, contacts, total }
};

export const importFubSelected = async (userId, people) => {
  assertCanWrite();
  const res = await authedFetch(`${FUB_FUNCTIONS_BASE}/fubImportSelected`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, people }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Import failed");
  }
  return res.json(); // { ok, imported }
};

export const updateAccessUser = async (userId, updatedData) => {
  assertCanWrite();
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, updatedData);
    return true;
  } catch (error) {
    console.error("Error updating profile:", error);
    return false;
  }
};

export const changeUserPassword = async (
  email,
  currentPassword,
  newPassword,
) => {
  const auth = getAuth();
  const user = auth.currentUser;

  // Create credential
  const credential = EmailAuthProvider.credential(email, currentPassword);

  // Step 1: Re-authenticate
  await reauthenticateWithCredential(user, credential);

  // Step 2: Update password
  await updatePassword(user, newPassword);
};

export const getuserinformation = async (collectionname, documentId) => {
  try {
    const docRef = doc(firestored, collectionname, documentId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      // Nothing written to the console: this runs on every launch, and a phone
      // keeps whatever is logged in memory (2026-09-14, speed wave 3).
      return docSnap.data();
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return null;
  }
};

// The Gmail refresh/access token now lives in a private subdoc
// (users/{uid}/private/gmail) so it is NOT exposed inside the broadly-readable
// users doc. Returns the token object, or null if Gmail isn't connected (or the
// read is blocked). Callers merge this into the in-memory user so every
// `user.gmailtoken.access_token` check keeps working unchanged.
export const getUserGmailToken = async (uid) => {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(firestored, "users", uid, "private", "gmail"));
    return snap.exists() ? snap.data()?.gmailtoken || null : null;
  } catch (error) {
    console.error("Error fetching Gmail token:", error);
    return null;
  }
};

// Loads a user's record AND merges in their Gmail token from the private
// subdoc. Falls back to the legacy users.gmailtoken field for any account not
// yet migrated. Use this everywhere the current user is (re)loaded into state,
// so the Gmail connection survives every profile reload.
export const loadCurrentUser = async (uid) => {
  // Both reads start together: this runs while the app shows "Loading..." on
  // open, and neither needs the other's answer. Each returns null on failure
  // instead of throwing, so running them side by side changes nothing else.
  const [userInfo, priv] = await Promise.all([
    getuserinformation("users", uid),
    getUserGmailToken(uid),
  ]);
  if (!userInfo) return null;
  return { ...userInfo, gmailtoken: priv || userInfo.gmailtoken || {} };
};

// Firestore's answers for "the server could not be reached" (the phone is offline, or
// the signal is too weak to get an answer in time).
// Exported because the Fast Lane reader (utils/fastUserRead.js) must judge the same error the same way: one
// rule, so the Can't Reach screen never depends on which reader answered.
const UNREACHABLE_CODES = new Set(["unavailable", "deadline-exceeded"]);
export const isUnreachable = (error) =>
  UNREACHABLE_CODES.has(error?.code) ||
  (typeof navigator !== "undefined" && navigator.onLine === false);

// App.js's lookup of the signed-in person when the app opens (2026-09-14, speed wave 4).
// Unlike loadCurrentUser, it tells "the server could not be reached" apart from "there
// is no record", so weak signal shows a Retry screen instead of the login page:
//   { user }                         the record, with the Gmail token merged in
//   { user: null }                   no users/{uid} record (or any other failure, as before)
//   { user: null, unreachable: true } the server could not be reached
// loadCurrentUser and getuserinformation are unchanged: the sign-in form, Settings and
// Contacts rely on them answering null for any failure.
export const loadCurrentUserAtLaunch = async (uid) => {
  const [record, priv] = await Promise.all([
    getDoc(doc(firestored, "users", uid)).then(
      (snap) => ({ data: snap.exists() ? snap.data() : null }),
      (error) => ({ error }),
    ),
    getUserGmailToken(uid),
  ]);
  if (record.error) {
    if (isUnreachable(record.error)) return { user: null, unreachable: true };
    console.error("Error fetching document:", record.error);
    return { user: null };
  }
  if (!record.data) return { user: null };
  return { user: { ...record.data, gmailtoken: priv || record.data.gmailtoken || {} } };
};

// Clears the Gmail token in the private subdoc (the disconnect flow also clears
// the legacy users doc fields via updateUserProfile).
export const clearUserGmailToken = async (uid) => {
  assertCanWrite();
  if (!uid) return;
  try {
    await setDoc(
      doc(firestored, "users", uid, "private", "gmail"),
      { gmailtoken: {} },
      { merge: true },
    );
  } catch (error) {
    console.error("Error clearing Gmail token:", error);
  }
};

export const getAllSlotsForChecking = async (
  table,
  parameter,
  comparison,
  value,
  parameter1,
  comparison1,
  value1,
) => {
  const items = await getDocs(
    query(
      collection(firestored, table),
      where(parameter, comparison, value),
      where(parameter1, comparison1, value1),
      where("status", "==", "Accepted"),
    ),
  );

  return items.docs.map((doc) => doc.data());
};

export const uploadImagesToFirebase = async (files) => {
  assertCanWrite();
  const uploadedUrls = [];

  for (let file of files) {
    const fileName = `dealPhotos/${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);

    const uploadTask = await uploadBytesResumable(storageRef, file);

    const downloadUrl = await getDownloadURL(uploadTask.ref);
    uploadedUrls.push(downloadUrl);
  }

  return uploadedUrls; // <-- array of URLs
};

export const saveitemwithid = async (table, id, data) => {
  assertCanWrite();
  // A saved deal or user makes the sign-in download of that list out of date.
  retireLaunchRead(table);
  return await setDoc(doc(firestored, table, id), data, { merge: true })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const getDealById = async (id) => {
  try {
    const dealRef = doc(firestored, "deals", id);
    const dealSnap = await getDoc(dealRef);

    if (dealSnap.exists()) {
      return { id: dealSnap.id, ...dealSnap.data() };
    } else {
      throw new Error("recipe not found");
    }
  } catch (error) {
    console.error("Error fetching recipe:", error);
    throw error;
  }
};

// Is there actually a deal document at this id? getDealById throws for BOTH
// "absent" and "couldn't read it", which callers deciding whether to RESTORE a
// deal must tell apart: updateDealById writes with setDoc(merge), so patching
// an absent deal CREATES one holding nothing but the patch. That is how a
// graduated listing lead landed on the board with no address, no client and no
// leadsource while the lead itself was marked converted and vanished from the
// pipeline (2026-08-07, 96 Orange Ave W -> dangling sourceDealId 1783354227389).
// Deliberately does NOT swallow read errors: a caller that cannot tell whether
// the deal is there must fail loudly rather than invent one.
export const dealExists = async (dealId) => {
  const snap = await getDoc(doc(firestored, "deals", String(dealId)));
  return snap.exists();
};

export const updateItem = async (table, id, data) => {
  assertCanWrite();
  // A saved deal or user makes the sign-in download of that list out of date.
  retireLaunchRead(table);
  return await setDoc(doc(firestored, table, id.toString()), data, {
    merge: true,
  })
    .then(() => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

export const resetpassword = async (email) => {
  return await sendPasswordResetEmail(auth, email)
    .then((response) => {
      return "success";
    })
    .catch((err) => {
      return err;
    });
};

// ✅ Save Lead Source
export const saveLeadSource = async (leadData) => {
  assertCanWrite();
  try {
    const docRef = doc(collection(firestored, "lead_sources"));

    await setDoc(docRef, {
      docId: docRef.id,
      abbreviation: leadData.abbreviation,
      name: leadData.name,
      leadCommission: leadData.leadCommission
        ? Number(leadData.leadCommission)
        : 0,
      createdAt: Date.now(),
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving lead source:", error);
    return { success: false, error: error.message };
  }
};

export const updateLeadSource = async (id, data) => {
  assertCanWrite();
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "lead_sources", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating leade:", error);
    return { success: false, error: error.message };
  }
};

// ✅ Delete Lead Source by ID
export const deleteLeadSource = async (id) => {
  assertCanWrite();
  try {
    await deleteDoc(doc(firestored, "lead_sources", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting lead source:", error);
    return { success: false, error: error.message };
  }
};

// ---- Buyer Leads -----------------------------------------------------------
const BUYER_LEADS_COLLECTION = "buyer_leads";

// Create a buyer lead with an auto-generated id. Stamps id/timestamps/defaults.
export const saveBuyerLead = async (data, opts = {}) => {
  assertCanWrite();
  try {
    const ref = doc(collection(firestored, BUYER_LEADS_COLLECTION));
    const now = Date.now();
    const lead = {
      ...ensureLeadOwner(data),
      id: ref.id,
      status: data.status || "active",
      stage: data.stage || "New Lead",
      createdAt: now,
      updatedAt: now,
      deletedAt: 0,
    };
    await setDoc(ref, lead);
    // Opt-in sync: a brand-new lead only pushes when its "Sync to Follow Up
    // Boss" switch was flipped on. (Updates still call the backend, which
    // makes the same check against the stored lead.)
    if (!opts.skipFubPush && lead.fubSync === true) pushLeadToFub(ref.id, "buyer");
    return { success: true, id: ref.id, lead };
  } catch (error) {
    console.error("Error saving buyer lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateBuyerLead = async (id, patch, opts = {}) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    if (!opts.skipFubPush) pushLeadToFub(id, "buyer");
    return { success: true, id };
  } catch (error) {
    console.error("Error updating buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Mirrors updateDealStageInFirebase — used by the board's drag-and-drop.
export const updateBuyerLeadStage = async (id, newStage, extra = {}) =>
  updateBuyerLead(id, { stage: newStage, ...extra });

// Soft delete (mirrors the deals `deletedAt` convention).
export const deleteBuyerLead = async (id) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, id), {
      deletedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Home's Follow-Ups Due box: only this person's buyer leads, from two narrow live
// queries (assigned to them, created by them), instead of every buyer lead on the team
// (2026-09-14, speed wave 4). Hands both back together: each lead once, the newest
// delivery of it, and soft-deleted rows left out, as listenBuyerLeads does.
export const listenMyBuyerLeads = (uid, callback) => {
  if (!uid) return () => {};
  const col = collection(firestored, BUYER_LEADS_COLLECTION);
  const lists = { assigned: [], created: [] };
  const watch = (field, key, otherKey) =>
    onSnapshot(query(col, where(field, "==", uid)), (snap) => {
      lists[key] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // The list that just arrived goes last, so its copy of a lead wins.
      const byId = new Map();
      [...lists[otherKey], ...lists[key]].forEach((row) => byId.set(row.id, row));
      callback([...byId.values()].filter((l) => !(Number(l.deletedAt) > 0)));
    });
  const stopAssigned = watch("assignedAgent", "assigned", "created");
  const stopCreated = watch("createdby", "created", "assigned");
  return () => {
    stopAssigned();
    stopCreated();
  };
};

// Real-time list (mirrors listenLeadSources). Filters out soft-deleted rows.
export const listenBuyerLeads = (callback) => {
  const q = query(
    collection(firestored, BUYER_LEADS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((l) => !(Number(l.deletedAt) > 0));
    callback(rows);
  });
};

// ---- Listing (seller) leads — mirrors the buyer_leads helpers above ----
const LISTING_LEADS_COLLECTION = "listing_leads";

export const saveListingLead = async (data, opts = {}) => {
  assertCanWrite();
  try {
    const ref = doc(collection(firestored, LISTING_LEADS_COLLECTION));
    const now = Date.now();
    const lead = {
      ...ensureLeadOwner(data),
      id: ref.id,
      status: data.status || "active",
      stage: data.stage || "New Lead",
      createdAt: now,
      updatedAt: now,
      deletedAt: 0,
    };
    await setDoc(ref, lead);
    // Opt-in sync: a brand-new lead only pushes when its "Sync to Follow Up
    // Boss" switch was flipped on. (Updates still call the backend, which
    // makes the same check against the stored lead.)
    if (!opts.skipFubPush && lead.fubSync === true)
      pushLeadToFub(ref.id, "listing");
    return { success: true, id: ref.id, lead };
  } catch (error) {
    console.error("Error saving listing lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateListingLead = async (id, patch, opts = {}) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    if (!opts.skipFubPush) pushLeadToFub(id, "listing");
    return { success: true, id };
  } catch (error) {
    console.error("Error updating listing lead:", error);
    return { success: false, error: error.message };
  }
};

export const updateListingLeadStage = async (id, newStage, extra = {}) =>
  updateListingLead(id, { stage: newStage, ...extra });

export const deleteListingLead = async (id) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, id), {
      deletedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting listing lead:", error);
    return { success: false, error: error.message };
  }
};

// --- Recently Deleted support for leads ---------------------------------
// Deals have had getDeletedDeals / restoreDeal / permanentlyDeleteDeal since
// the Recently Deleted page shipped; leads never did, so a soft-deleted lead
// could be found but not brought back (2026-08-07, 96 Orange Ave W needed a
// hand-written script). Same `deletedAt` convention: 0/falsy = live.

const deletedFrom = async (table) => {
  const snap = await getDocs(collection(firestored, table));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => Number(r.deletedAt) > 0)
    .sort((a, b) => Number(b.deletedAt || 0) - Number(a.deletedAt || 0));
};

export const getDeletedListingLeads = () => deletedFrom(LISTING_LEADS_COLLECTION);
export const getDeletedBuyerLeads = () => deletedFrom(BUYER_LEADS_COLLECTION);

// Restore clears ONLY deletedAt — stage, notes, agent and status all stay as
// they were, matching how 96 Orange was repaired by hand.
//
// All four return `true` on success. recently-deleted.jsx checks `if (!ok)` on
// the result, so returning setDoc's undefined would report a failure on every
// successful restore.
const setDeletedAt = async (table, id, value) => {
  await setDoc(doc(firestored, table, String(id)), { deletedAt: value }, { merge: true });
  return true;
};

// After the restore, a client the Follow Up Boss Leftovers card archived comes back
// with its lead (fubLeftoversStore.js). Best-effort: the lead is already live.
// Loaded on demand because fubLeftoversStore.js imports this file.
export const restoreListingLead = async (id) => {
  const ok = await setDeletedAt(LISTING_LEADS_COLLECTION, id, 0);
  try {
    const snap = await getDoc(doc(firestored, LISTING_LEADS_COLLECTION, String(id)));
    const clientIds = snap.exists() ? listClients(snap.data()).map((c) => c.contactId) : [];
    if (clientIds.length) {
      const { unarchiveFubLeftoverContacts } = await import("./fubLeftoversStore");
      await unarchiveFubLeftoverContacts(clientIds);
    }
  } catch (err) {
    console.error("Bringing back a restored listing lead's archived clients failed:", err);
  }
  return ok;
};

export const restoreBuyerLead = async (id) => {
  const ok = await setDeletedAt(BUYER_LEADS_COLLECTION, id, 0);
  try {
    const snap = await getDoc(doc(firestored, BUYER_LEADS_COLLECTION, String(id)));
    const clientIds = snap.exists() ? listClients(snap.data()).map((c) => c.contactId) : [];
    if (clientIds.length) {
      const { unarchiveFubLeftoverContacts } = await import("./fubLeftoversStore");
      await unarchiveFubLeftoverContacts(clientIds);
    }
  } catch (err) {
    console.error("Bringing back a restored buyer lead's archived clients failed:", err);
  }
  return ok;
};

export const permanentlyDeleteListingLead = async (id) => {
  assertCanWrite();
  await deleteDoc(doc(firestored, LISTING_LEADS_COLLECTION, String(id)));
  return true;
};

export const permanentlyDeleteBuyerLead = async (id) => {
  assertCanWrite();
  await deleteDoc(doc(firestored, BUYER_LEADS_COLLECTION, String(id)));
  return true;
};

export const listenListingLeads = (callback) => {
  const q = query(
    collection(firestored, LISTING_LEADS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => {
        const row = { id: d.id, ...d.data() };
        // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
        row.propertyAddress = stripCountry(row.propertyAddress);
        return row;
      })
      .filter((l) => !(Number(l.deletedAt) > 0));
    callback(rows);
  });
};

// One-time cleanup (admin button): archive leads + appointments whose property
// is already on the board — they converted, but nobody flipped their status,
// so they linger at the bottom of the Status Sheet and in the pipelines.
// The matching lives in planStatusCleanup (statusSheetData.js — pure, tested).
// With { dryRun: true } it only RETURNS the plan (nothing written) so the
// caller can show the admin exactly what will change and confirm first.
// Every write is a recoverable status flip — nothing is deleted.
export const archiveConvertedRecords = async ({ dryRun = false } = {}) => {
  assertCanWrite();
  const fetchAll = async (name) => {
    const snap = await getDocs(query(collection(firestored, name)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const [deals, listingLeads, buyerLeads, appointments] = await Promise.all([
    fetchAll("deals"),
    fetchAll(LISTING_LEADS_COLLECTION),
    fetchAll(BUYER_LEADS_COLLECTION),
    fetchAll("listingAppointments"),
  ]);
  const plan = planStatusCleanup({ deals, listingLeads, buyerLeads, appointments });
  if (dryRun) return { ...plan, applied: false };

  const now = Date.now();
  const results = await Promise.allSettled([
    ...plan.listingLeadsToArchive.map((l) =>
      updateDoc(doc(firestored, LISTING_LEADS_COLLECTION, l.id), {
        status: "converted",
        outcome: "won",
        convertedAt: now,
        archivedBy: "status-cleanup",
      }),
    ),
    ...plan.buyerLeadsToArchive.map((l) =>
      updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, l.id), {
        status: "converted",
        outcome: "won",
        convertedAt: now,
        archivedBy: "status-cleanup",
      }),
    ),
    ...plan.appointmentsToWin.map((a) =>
      updateDoc(doc(firestored, "listingAppointments", a.id), {
        status: "won",
        archivedBy: "status-cleanup",
      }),
    ),
  ]);
  const failed = results.filter((r) => r.status === "rejected").length;
  return { ...plan, applied: true, failed };
};

// Admin button: find duplicate lead cards (same contact, or same name+address
// for listings) and merge each cluster into the survivor the admin picked.
// dryRun returns { clusters } only (nothing written) so the modal can show the
// side-by-side preview. `choices` maps cluster.key -> survivorId | "skip".
// Merges are recoverable: non-survivors are soft-deleted (deletedAt) and stamped
// mergedIntoLeadId so any merge can be traced/undone.
export const mergeDuplicateLeads = async ({ dryRun = false, choices = {} } = {}) => {
  assertCanWrite();
  const fetchAll = async (name) => {
    const snap = await getDocs(query(collection(firestored, name)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };
  const [listingLeads, buyerLeads] = await Promise.all([
    fetchAll(LISTING_LEADS_COLLECTION),
    fetchAll(BUYER_LEADS_COLLECTION),
  ]);
  const { clusters } = planDuplicateMerge({ listingLeads, buyerLeads });
  if (dryRun) return { clusters, applied: false };

  const now = Date.now();
  const writes = [];
  let mergedClusters = 0;
  clusters.forEach((cluster) => {
    const choice = choices[cluster.key];
    if (!choice || choice === "skip") return;
    const merged = computeMerge(cluster, choice);
    // Chosen survivor is no longer in this cluster (data shifted since preview) —
    // skip rather than merge around a survivor the admin never picked.
    if (!merged) return;
    const { survivorId, survivorPatch, archivedIds } = merged;
    mergedClusters += 1;
    writes.push(
      updateDoc(doc(firestored, cluster.collection, survivorId), {
        coClients: survivorPatch.coClients,
        notes: survivorPatch.notes,
        dedupMergedAt: now,
      }),
    );
    archivedIds.forEach((id) =>
      writes.push(
        updateDoc(doc(firestored, cluster.collection, id), {
          deletedAt: now,
          deleted: true,
          mergedIntoLeadId: survivorId,
          archivedBy: "duplicate-merge",
        }),
      ),
    );
  });
  const results = await Promise.allSettled(writes);
  const failed = results.filter((r) => r.status === "rejected").length;
  return { clusters, applied: true, mergedClusters, failed };
};

// Graduate a listing (seller) lead into a Deal on the Deals Board at "Pre-Market".
// Mirrors convertBuyerLeadToDeal but seller-side; the listing card then leaves the
// pipeline (status "converted") and a Seller deal appears on the board.
// Settings > Team Defaults for a NEW deal's broker admin fee. A failed read
// returns null (withDefaultAdminFee then uses $599): settings trouble must
// never block creating a deal.
const teamDefaultsForNewDeal = async () => {
  try {
    return await loadTeamDefaults();
  } catch (e) {
    console.error("Team Defaults unavailable, using the $599 admin fee:", e);
    return null;
  }
};

// Who a new file's checklist items go to (My Checklist spec 09/27/2026, decisions 4 and 13): the
// agent's Listing Coordinator and Client Care person, falling back to the team's Backup Client Care
// Person. These seeders run outside any screen, so they read the team list themselves. A failed read
// seeds with no owners (the items land in the red box) and never blocks creating the deal.
const seedOwnersFor = async (agentId, teamDefaults) => {
  try {
    const snap = await getDocs(collection(firestored, "users"));
    const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return coordinatorsFor(agentId, users, teamDefaults && teamDefaults.clientCareOwnerId);
  } catch (e) {
    console.error("Checklist owners unavailable, seeding without owners:", e);
    return {};
  }
};

export const convertListingLeadToDeal = async (lead, overrides = {}, user = {}) => {
  try {
    // Lossless graduation: a lead that was moved in from an existing deal
    // carries its origin (sourceDealId). Restore that original deal — full
    // data intact — straight to Pre-Market rather than building a sparser one.
    //
    // Only take the shortcut when that deal is REALLY still there. A dangling
    // pointer used to fall straight through this branch: the stub read failed,
    // was swallowed, and the merge-write below then CREATED the deal out of the
    // patch alone — no address, no client, no leadsource — while the lead was
    // marked converted and left the pipeline. Absent source deal, build the
    // deal from the lead instead (the branch below), which is complete.
    if (lead.sourceDealId && (await dealExists(lead.sourceDealId))) {
      // Merge, never overwrite: the intake stub may hold clients the lead
      // doesn't (and vice versa) — restoring it must keep everyone. A failed
      // stub read degrades to just the lead's clients.
      let stub = null;
      try {
        stub = await getDealById(String(lead.sourceDealId));
      } catch (e) {
        console.error("Couldn't read the source deal for the client merge:", e);
      }
      const patch = {
        deletedAt: 0,
        dealStage: "Pre-Market",
        migratedToLeadId: "",
        // Signed = real board deal — clear the file-request stub markers so it leaves the Pending File Creation queue.
        status: "Approved",
        requestType: "",
        // Link the restored deal back to its lead so Lead History renders.
        sourceLeadId: String(lead.id || ""),
        customers: mergeClientIds(
          stub && stub.customers,
          listClients(lead).map((c) => c.contactId).filter(Boolean),
        ),
      };
      if (lead.notes) patch.notes = lead.notes; // carry any pipeline note edits
      // Henry 10/06/2026: the lead's Estimate of Proceeds rides onto the listing (links intact, so the deal's
      // figures take over), unless the file already has its own estimate.
      const carriedNetSheet = carryNetSheetToDeal(lead.netSheet);
      if (carriedNetSheet && !(stub && stub.netSheet)) patch.netSheet = carriedNetSheet;
      // Target Coming Soon date from the graduation dialog — the Pre-Market
      // column's year sections bucket on this field (preMarketGrouping.js).
      if (overrides.listingAppointment)
        patch.listingAppointment = overrides.listingAppointment;
      // The lead's agent wins on graduation — heals older file-requests that
      // were stamped with their creator instead of the chosen agent.
      if (lead.assignedAgent) {
        patch.agentData = lead.assignedAgent;
        patch.agentName = lead.assignedAgentName || "";
      }
      // A signed listing needs the same checklist a converted buyer gets. The
      // intake stub only ever copied in the Prospect stage (and stamped
      // taskStatusTracker.Prospect, so no later top-up pass revisits it), which
      // left graduated listings sitting on the board holding nothing. Seed off
      // the deal as it will stand AFTER the patch — dealStage decides how far
      // up the checklist runs. buildDealBackfill keys "already has this stage"
      // off the deal's real tasks, so re-running can never duplicate.
      try {
        // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
        const template = await getTaskTemplate();
        const seeded = { ...(stub || {}), ...patch };
        const owners = await seedOwnersFor(seeded.agentData, await teamDefaultsForNewDeal());
        const backfill = buildDealBackfill(seeded, template || {}, {
          ...owners,
          stageOrder: STAGE_ORDER,
          nowIso: new Date().toISOString(),
        });
        if (backfill) {
          patch.tasks = backfill.tasks;
          patch.taskStatusTracker = backfill.taskStatusTracker;
        }
      } catch (e) {
        console.error("Listing graduation: task seeding failed, restoring deal without checklist:", e);
      }
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      await updateDealById(String(lead.sourceDealId), patch);
      await updateListingLead(lead.id, {
        status: "converted",
        outcome: "won",
        convertedDealId: String(lead.sourceDealId),
        convertedAt: Date.now(),
      });
      // The signed listing means the appointment was won — log it itself.
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      classifyAppointmentForLead(lead, "won");
      return { success: true, dealId: String(lead.sourceDealId) };
    }

    const nowId = Date.now();
    const teamDefaults = await teamDefaultsForNewDeal();
    const base = buildDealFromLead(
      lead,
      {
        propertyaddress: lead.propertyAddress || "",
        offerPrice: lead.listPrice || lead.estValue || "",
        // Only a real fee the caller passes rides in; otherwise the default.
        brokeradminfee: overrides.brokeradminfee,
      },
      { user, nowId, teamDefaults },
    );
    let newdata = {
      ...base,
      dealtype: "Seller",
      dealStage: "Pre-Market",
      // A signed listing is a real board deal — NOT a pending file-request
      // stub (the old "fileRequest"/"Pending" shape rendered an empty card).
      status: "Approved",
      requestType: "",
      clientLastName:
        base.clientLastName ||
        (lead.contactName || "").trim().split(/\s+/).slice(-1)[0] ||
        "",
      leadsource: lead.source || "",
      listPrice: lead.listPrice || "",
      suggestedPrice: lead.estValue || "",
      listingDate: lead.appointmentAt || "",
      notes: lead.notes || "",
      // Henry 10/06/2026: the lead's Estimate of Proceeds rides onto the listing (links intact).
      ...(carryNetSheetToDeal(lead.netSheet) ? { netSheet: carryNetSheetToDeal(lead.netSheet) } : {}),
      ...overrides,
    };
    // A new deal: a missing broker admin fee takes the Team Defaults figure.
    newdata = withDefaultAdminFee(newdata, teamDefaults);
    // buildDealFromLead leaves tasks empty on purpose — seeding is the caller's
    // job (same as convertBuyerLeadToDeal). Without this a graduated listing
    // landed on the board with no checklist at all.
    let seeded = newdata;
    try {
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      const template = await getTaskTemplate();
      const backfill = buildDealBackfill(newdata, template || {}, {
        ...(await seedOwnersFor(newdata.agentData, teamDefaults)),
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) seeded = { ...newdata, ...backfill };
    } catch (e) {
      console.error("Listing-lead conversion: task seeding failed, creating deal without checklist:", e);
    }
    const res = await saveitemwithid("deals", String(nowId), seeded);
    // saveitemwithid swallows errors (returns the error object instead of
    // throwing). Only mark the lead converted once the deal is truly saved,
    // so a failed write can never orphan the lead off the board.
    if (res !== "success") {
      throw new Error((res && res.message) || "Failed to create the deal");
    }
    await updateListingLead(lead.id, {
      status: "converted",
        outcome: "won",
      convertedDealId: String(nowId),
      convertedAt: nowId,
    });
    // The signed listing means the appointment was won — log it itself.
    // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
    classifyAppointmentForLead(lead, "won");
    return { success: true, dealId: String(nowId), deal: seeded };
  } catch (error) {
    console.error("Error graduating listing lead to deal:", error);
    return { success: false, error: error.message };
  }
};

// Convert a won lead into a Deal in "Under Contract", seeding its checklist the
// same way a new deal does, then mark the lead converted. Deal is created FIRST;
// the lead is only flagged converted on success (no orphaned "converted" lead).
export const convertBuyerLeadToDeal = async (lead, overrides = {}, user = {}) => {
  try {
    // Lossless: a buyer lead moved in from an existing deal restores that
    // original deal (full data) to Under Contract rather than building a new one.
    //
    // Only take the shortcut when that deal REALLY still exists. A dangling
    // pointer (e.g. the stub was purged) would otherwise fall through here, the
    // merge-write below would CREATE the deal from the patch alone — no address,
    // client, agent or leadsource — and the lead would be marked converted and
    // vanish from the pipeline. This mirrors the listing-side fix (96 Orange).
    if (lead.sourceDealId && (await dealExists(lead.sourceDealId))) {
      // Merge, never overwrite: the stub may hold a partner the lead dropped
      // (or the lead gained a co-client after intake) — everyone survives. A
      // failed stub read degrades to just the lead's clients.
      let stub = null;
      try {
        stub = await getDealById(String(lead.sourceDealId));
      } catch (e) {
        console.error("Couldn't read the source deal for the client merge:", e);
      }
      const patch = {
        deletedAt: 0,
        dealStage: "Under Contract",
        migratedToLeadId: "",
        // Signed = real board deal — clear the file-request stub markers so it leaves the Pending File Creation queue.
        status: "Approved",
        requestType: "",
        // Link the restored deal back to its lead so Lead History renders.
        sourceLeadId: String(lead.id || ""),
        customers: mergeClientIds(
          stub && stub.customers,
          listClients(lead).map((c) => c.contactId).filter(Boolean),
        ),
        agentData: lead.assignedAgent || "",
        agentName: lead.assignedAgentName || "",
      };
      if (lead.notes) patch.notes = lead.notes;
      // Henry 10/06/2026: the lead's Buyer Agreement Expires date wins over the file's copy.
      if (lead.buyerAgreementExpiration) patch.buyerAgreementExpiration = lead.buyerAgreementExpiration;
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      await updateDealById(String(lead.sourceDealId), patch);
      await updateBuyerLead(lead.id, {
        status: "converted",
        outcome: "won",
        convertedDealId: String(lead.sourceDealId),
        convertedAt: Date.now(),
      });
      return { success: true, dealId: String(lead.sourceDealId) };
    }

    const nowId = Date.now();
    const teamDefaults = await teamDefaultsForNewDeal();
    const base = buildDealFromLead(lead, overrides, {
      user,
      nowId,
      teamDefaults,
    });

    let newdata = base;
    try {
      // Side-aware (09-10 fix): a buyer file seeds from the Buyer template,
      // falling back to the listing one only while the Buyer template is empty.
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      const template = await getTaskTemplateForSide(base.dealtype);
      const backfill = buildDealBackfill(base, template || {}, {
        ...(await seedOwnersFor(base.agentData, teamDefaults)),
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) newdata = { ...base, ...backfill };
    } catch (e) {
      console.error("Buyer-lead conversion: task seeding failed, creating deal without checklist:", e);
    }

    await saveitemwithid("deals", String(nowId), newdata);
    await updateBuyerLead(lead.id, {
      status: "converted",
        outcome: "won",
      convertedDealId: String(nowId),
      convertedAt: nowId,
    });
    return { success: true, dealId: String(nowId), deal: newdata };
  } catch (error) {
    console.error("Error converting buyer lead to deal:", error);
    return { success: false, error: error.message };
  }
};

// Create a "file request" deal straight from a pipeline lead (buyer or seller)
// so an agent can request the file while adding the lead — it lands in the
// Pending File Creation queue. Mirrors the fileRequest shape built in
// main-list-request, populated from the lead, with the Prospect checklist seeded.
export const createFileRequestFromLead = async (
  lead = {},
  side = "buyer",
  user = {},
  details = {},
) => {
  try {
    const nowId = Date.now();
    const isSeller = side === "seller";
    const lastName =
      (lead.contactName || "").trim().split(/\s+/).slice(-1)[0] || "";

    const base = {
      status: "Pending",
      requestType: "fileRequest",
      dealtype: isSeller ? "Seller" : "Buyer",
      dealStage: "CMAs",
      customers: lead.contactId ? [lead.contactId] : [],
      clientLastName: lastName,
      agentData: lead.assignedAgent || "",
      agentName: lead.assignedAgentName || "",
      leadsource: lead.leadSource || lead.source || "",
      leadCommission: "",
      propertyaddress: isSeller ? lead.propertyAddress || "" : "",
      propertyLocation: null,
      notes: lead.notes || "",
      createdby: user.id || "",
      createdat: nowId,
      sourceLeadId: lead.id || "",
      // File-request specifics — left for the team to fill when building.
      fileFormat: "",
      fileDeadline: "",
      // Property/transaction type — drives which Client Portal forms the client
      // fills out (formsForRequest). Carried from the lead so a condo/pool/multi-
      // family file-request assembles its tailored sections, not the generic set.
      formType: lead.formType || "",
      // Buyer side (Henry 10/06/2026): the buyer agreement's expiration date
      // rides from the lead onto the file (a dialog answer overlays it below).
      ...(isSeller ? {} : { buyerAgreementExpiration: lead.buyerAgreementExpiration || "" }),
      // Standard deal fields kept present (blank) so no deals view hits undefined.
      propertyphoto: [],
      propertyType: "",
      financingType: "",
      earnestMoney: "",
      offerPrice: "",
      closingdate: "",
      inspection: "",
      inspectionDeadline: "",
      financingDeadline: "",
      totalcommissionrate: "",
      agentCommission: "",
      agentCommissionamount: "",
      fixedagentcommission: "",
      brokeradminfee: "",
      captype: "",
      captypedescription: "",
      splittype: "TeamSplit",
      splitdescription: "Team Member Split",
      inhousereferral: false,
      teamleadName: "",
      assignedteamlead: "",
      flatfee: "",
      flatfeeamount: "",
      tasks: [],
      taskStatusTracker: {},
    };

    // Request-dialog answers (file format, deadline, trust, disclosure, …)
    // overlay the blanks so the team gets a complete request, not a shell.
    // Whitelisted merge — see utils/fileRequestDetails.js.
    let newdata = applyFileRequestDetails(base, details);
    // A new file request is a new deal document: it carries the Team Defaults
    // broker admin fee instead of the old blank "".
    const teamDefaults = await teamDefaultsForNewDeal();
    newdata = withDefaultAdminFee(newdata, teamDefaults);
    try {
      // Side-aware: a buyer file request never seeds from the listing template.
      // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
      const template = await getTaskTemplateForSide(newdata.dealtype);
      const backfill = buildDealBackfill(newdata, template || {}, {
        ...(await seedOwnersFor(newdata.agentData, teamDefaults)),
        stageOrder: STAGE_ORDER,
        nowIso: new Date().toISOString(),
      });
      if (backfill) newdata = { ...newdata, ...backfill };
    } catch (e) {
      console.error("File-request task seeding failed, creating without checklist:", e);
    }

    const result = await saveitemwithid("deals", String(nowId), newdata);
    // Hand the saved deal back so callers can feed it to the email/notification
    // helpers without rebuilding the shape.
    return { success: result === "success", dealId: String(nowId), deal: newdata };
  } catch (error) {
    console.error("createFileRequestFromLead failed:", error);
    return { success: false, error: error.message };
  }
};

// One-click Feed post of a buyer's NEEDS (no client name), attributed to the
// agent. Records feedPostId on the lead so the UI can show "Posted".
export const postBuyerNeedToFeed = async (lead, user = {}) => {
  try {
    const agentName =
      [user.firstname, user.lastname].filter(Boolean).join(" ") ||
      user.name ||
      "";
    const text = buildBuyerNeedPost(lead);
    const post = await createPost({
      id: generatePostId(),
      authorId: user.id,
      authorName: agentName,
      authorRole: user.realRole || user.role || "",
      text,
    });
    await updateBuyerLead(lead.id, {
      feedPostId: post.id,
      postedToFeedAt: Date.now(),
    });
    // A buyer need on the Feed is a post like any other: the team hears about it (10/05/2026).
    alertTeamAboutPostInBackground(post.id);
    return { success: true, post };
  } catch (error) {
    console.error("Error posting buyer need to feed:", error);
    return { success: false, error: error.message };
  }
};

// Append an activity to a buyer lead, stamp lastContacted (ISO date), and
// optionally set the next follow-up. arrayUnion keeps the append atomic.
// `arrayUnion`/`updateDoc`/`doc`/`BUYER_LEADS_COLLECTION` are already in this file.
export const logBuyerActivity = async (leadId, activity, { nextFollowUp } = {}) => {
  assertCanWrite();
  try {
    const patch = {
      activities: arrayUnion(activity),
      lastContacted: activityDateIso(activity.at),
      updatedAt: Date.now(),
    };
    if (nextFollowUp) patch.nextFollowUp = nextFollowUp;
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), patch);
    return { success: true };
  } catch (error) {
    console.error("Error logging buyer activity:", error);
    return { success: false, error: error.message };
  }
};

// Snooze a follow-up: push nextFollowUp forward and append a note to the
// timeline, WITHOUT touching lastContacted — a snooze is not a contact, so it
// must not make the lead look like it was just reached.
// Dismiss a due follow-up outright: clears the date (the lead drops off the
// Follow-Ups Due widget) and leaves a breadcrumb on the lead's timeline.
export const dismissBuyerFollowUp = async (leadId, activity) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), {
      activities: arrayUnion(activity),
      nextFollowUp: "",
      updatedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error dismissing follow-up:", error);
    return { success: false, error: error.message };
  }
};

export const snoozeBuyerLead = async (leadId, activity, nextFollowUp) => {
  assertCanWrite();
  try {
    const patch = {
      activities: arrayUnion(activity),
      updatedAt: Date.now(),
    };
    if (nextFollowUp) patch.nextFollowUp = nextFollowUp;
    await updateDoc(doc(firestored, BUYER_LEADS_COLLECTION, leadId), patch);
    return { success: true };
  } catch (error) {
    console.error("Error snoozing buyer lead:", error);
    return { success: false, error: error.message };
  }
};

// Real-time listener
export const listenLeadSources = (callback) => {
  const q = query(
    collection(firestored, "lead_sources"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

// The team list. The dashboard shell passes { shared: true, uid, consumer } on
// launch to reuse the download App.js started at sign-in (utils/launchData.js).
// Every other call reads fresh, and a fresh read of the team list retires the
// sign-in copy, since it usually follows a save.
export const getAllusers = async (table, { shared = false, uid, consumer } = {}) => {
  const launchCopy =
    shared && table === "users" ? claimLaunchRead("users", { uid, consumer }) : null;
  if (!shared && table === "users") retireLaunchRead("users");
  const items = launchCopy
    ? await launchCopy
    : await getDocs(query(collection(firestored, table)));

  return items.docs.map((doc) => doc.data());
};

// Starts the two big downloads (every deal, the team list) as soon as App.js has a
// confirmed sign-in, so the first screens can reuse them instead of starting them
// later. They no longer hold that person's own record back: it is read on a quick
// connection of its own (utils/fastUserRead.js), after sharing one with these downloads
// kept "Loading..." up for the whole 30 MB of deals (measured 09/15/2026). Raw reads only: nothing here
// writes (the auto-archive in getAlldealforAdmin runs when a screen uses the data,
// after App.js has checked the account). See utils/launchData.js for the rules.
export const startLaunchReads = (uid) =>
  startLaunchData(uid, {
    deals: () => getDocs(query(collection(firestored, "deals"))),
    users: () => getDocs(query(collection(firestored, "users"))),
  });
// Google Places appends ", USA" to every address — strip it once at load
// time so every screen shows clean addresses.
const stripCountry = (s) =>
  typeof s === "string" ? s.replace(/,\s*(USA|United States)\b/gi, "") : s;

// The dashboard's File Requests queue.
//
// Deliberately NOT getAlldealforAdmin: that drops every soft-deleted deal, and a
// file request is soft-deleted the instant it's created (the deal is just a
// holder for the Drive folder while the real record lives in a pipeline). Read
// the collection raw and let selectPendingFileRequests decide what belongs.
// Is this property already in the system? Reads FRESH rather than using the
// in-memory deals list, because the duplicate is often minutes old and entered
// by someone else (3180 Cuneen Trail: Joe at 11:19am, Merri at 2:02pm).
//
// Fails OPEN: if the lookup errors we return no matches, so a warning check can
// never be the reason a legitimate request cannot be submitted.
export const findDuplicatesForAddress = async (address, opts = {}) => {
  try {
    if (!address) return [];
    const [dealSnap, leadSnap] = await Promise.all([
      getDocs(query(collection(firestored, "deals"))),
      getDocs(query(collection(firestored, LISTING_LEADS_COLLECTION))),
    ]);
    return findAddressMatches({
      address,
      deals: dealSnap.docs.map((d) => d.data()),
      listingLeads: leadSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      ...opts,
    });
  } catch (error) {
    console.error("Duplicate address check failed:", error);
    return [];
  }
};

// The whole `deals` collection. Launch-time loads pass { shared: true } so the
// dashboard shell, the home screen and the File Requests queue, which all ask in
// the same instant, share ONE download instead of pulling every deal three times
// (see sharedRead.js). A launch-time load that also names the signed-in person and
// the screen asking ({ uid, consumer }) first reuses the download App.js started at
// sign-in, even if that download has already finished (see launchData.js).
// Refreshes after a save leave `shared` off and always read fresh, and a fresh read
// retires the sign-in copy so no later screen is handed data from before the save.
const readAllDeals = ({ shared = false, uid, consumer } = {}) => {
  const read = () => getDocs(query(collection(firestored, "deals")));
  if (!shared) {
    retireLaunchRead("deals");
    return read();
  }
  return claimLaunchRead("deals", { uid, consumer }) || shareRead("deals:all", read);
};

export const getPendingFileRequests = async ({ shared = false, uid, consumer } = {}) => {
  try {
    const items = await readAllDeals({ shared, uid, consumer });
    const deals = items.docs.map((doc) => {
      const d = doc.data();
      return { ...d, propertyaddress: stripCountry(d.propertyaddress) };
    });
    return selectPendingFileRequests(deals);
  } catch (error) {
    console.error("Error loading pending file requests:", error);
    return [];
  }
};

export const getAlldealforAdmin = async (table, adminid, { shared = false, uid, consumer } = {}) => {
  const items =
    table === "deals"
      ? await readAllDeals({ shared, uid, consumer })
      : await getDocs(
          query(
            collection(firestored, table),
            // where("assignedteamlead", "==", adminid)
          ),
        );

  const deals = items.docs
    .map((doc) => {
      const d = doc.data();
      return { ...d, propertyaddress: stripCountry(d.propertyaddress) };
    })
    .filter((d) => !(typeof d.deletedAt === "number" && d.deletedAt > 0))
    .sort((a, b) => (a.closingdate || "").localeCompare(b.closingdate || ""));

  // Auto-archive cancelled deals that have aged out (2+ weeks since cancel, or
  // legacy cancelled deals with no cancelledAt). Fire-and-forget and idempotent
  // — already-archived deals are skipped, so re-running is harmless. We also
  // flip `archived` on the returned objects so the UI hides them immediately
  // without waiting for a refetch.
  if (table === "deals") {
    const due = getDealsToAutoArchive(deals, Date.now());
    if (due.length) {
      const dueSet = new Set(due.map(String));
      Promise.all(
        deals
          .filter((d) => dueSet.has(String(d.id ?? d.createdat)))
          .map((d) => {
            const id = d.id ?? d.createdat;
            // Every due deal is Cancelled, so this also clears a door code (archivePatchFor).
            // The same patch goes on this screen's copy, which keeps the write launch-copy-safe.
            const patch = archivePatchFor(d, true);
            Object.assign(d, patch);
            return writeDealArchived(id, patch).catch((e) =>
              console.error("Auto-archive failed for deal", id, e),
            );
          }),
      );
    }
  }

  return deals;
};

// Deal stages that mean a listing has been won (contract signed, going to market
// or further). "Closed" is treated separately as its own appointment status.
const LISTING_WON_STAGES = [
  "Pre-Market",
  "Coming Soon",
  "Under Contract",
  "Pending",
  "Active",
];

// Stages that count as "listed" — first time a deal hits one of these, we
// stamp listedDate. Pending is excluded (it's the post-UC closing window).
const LISTED_STAGES = ["Pre-Market", "Coming Soon", "Active"];

export const updateDealStageInFirebase = async (
  dealId,
  newStage,
  extraData = {}
) => {
  assertCanWrite();
  try {
    const dealRef = doc(firestored, "deals", dealId);

    // Stamp first-time-reached timestamps so cycle-time math works without
    // a backend stage-history table. Only set if not already present.
    const snap = await getDoc(dealRef);
    const existing = snap.exists() ? snap.data() : {};
    const stamps = {};
    const nowIso = new Date().toISOString();
    if (LISTED_STAGES.includes(newStage) && !existing.listedDate) {
      stamps.listedDate = nowIso;
    }
    if (newStage === "Under Contract" && !existing.underContractDate) {
      stamps.underContractDate = nowIso;
    }
    if (newStage === "Closed" && !existing.closedDate) {
      stamps.closedDate = nowIso;
    }
    // Auto-archive clock: stamp when the deal enters "Cancelled" (reset each
    // time so re-cancelling restarts the 2 weeks); clear it when it leaves.
    if (newStage === "Cancelled") {
      stamps.cancelledAt = Date.now();
    } else if (existing.cancelledAt) {
      stamps.cancelledAt = null;
    }
    // Listing Prep (spec 2026-09-25, section 4): the day a deal entered each stage, so
    // "3 days after stage starts" has something to count from. Kept per stage, never overwritten
    // on a re-pick of the same stage.
    Object.assign(stamps, stageEntryPatch(existing, existing.dealStage, newStage, prepTodayYMD()));

    retireLaunchRead("deals");
    await updateDoc(dealRef, {
      dealStage: newStage,
      ...extraData,
      // Stamps last, so no caller's extraData can overwrite a date this function set.
      ...stamps,
    });

    let newApptStatus = null;
    if (newStage === "Closed") newApptStatus = "closed";
    else if (LISTING_WON_STAGES.includes(newStage)) newApptStatus = "won";

    if (newApptStatus) {
      const apptsSnap = await getDocs(
        query(
          collection(firestored, "listingAppointments"),
          where("dealId", "==", String(dealId)),
        ),
      );
      await Promise.all(
        apptsSnap.docs.map((d) =>
          updateDoc(doc(firestored, "listingAppointments", d.id), {
            status: newApptStatus,
          }),
        ),
      );
    }
  } catch (error) {
    console.error("Error updating deal stage:", error);
  }
};

export const getSourceLead = async () => {
  const snapshot = await getDocs(collection(firestored, "lead_sources"));
  // Returned without writing the list to the console (read on every launch).
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

export const updateIsShowStat = async (userId, value) => {
  assertCanWrite();
  try {
    const userRef = doc(firestored, "users", userId);
    await updateDoc(userRef, { isShowStats: value });
    console.log(`✅ isShowStat updated for user ${userId}:`, value);
    return true;
  } catch (error) {
    console.error("❌ Error updating isShowStat:", error);
    return false;
  }
};

export const getUserById = async (id) => {
  const docRef = doc(firestored, "users", id);
  const docSnap = await getDoc(docRef);

  return docSnap.exists() ? docSnap.data() : null;
};

export const getUsers = async () => {
  const snapshot = await getDocs(collection(firestored, "users"));
  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  console.log("🔥 Direct Firestore test:", data);
  return data;
};

export const uploadImageToUser = async (imageFile, path) => {
  assertCanWrite();
  if (!imageFile) return "";

  const storageRef = ref(storage, `${path}/${Date.now()}-${imageFile.name}`);
  const snapshot = await uploadBytes(storageRef, imageFile);
  const downloadURL = await getDownloadURL(snapshot.ref);

  return downloadURL;
};

// Lets a failed write reach the caller. This used to `.catch()` and RETURN the
// error, so `await saveitemwithkey(...)` never threw and the deal chat's
// try/catch could never fire — a refused write looked identical to a good one.
// Firestore shows a local write before the server confirms it, so the message
// appeared, spun, then silently vanished on rollback with nothing explaining
// why (2026-08-02, Henry: "took a long time to spin and then disappear").
export const saveitemwithkey = async (collection, key, chatdata) => {
  assertCanWrite();
  await setDoc(doc(firestored, collection, key), chatdata, { merge: true });
  return "success";
};

export const getAllEmployeeTasks = async (
  table,
  parameter,
  comparison,
  value,
) => {
  const items = await getDocs(
    query(collection(firestored, table), where(parameter, comparison, value)),
  );

  return items.docs.map((doc) => doc.data());
};

export const addChatIdToDeal = async (dealId, chatId) => {
  assertCanWrite();
  try {
    const dealRef = doc(firestored, "deals", dealId);

    retireLaunchRead("deals");
    await updateDoc(dealRef, {
      chatId: chatId,
    });

    console.log("Chat ID added successfully");
  } catch (error) {
    console.error("Error adding chat ID:", error);
  }
};

// The checklist as saved. While Firebase believes it is offline (after a connection broke, until
// it reconnects) it answers a read from its memory copy, and that copy can be older than a tick
// made a moment ago: a transaction's write never refreshes it. That is not the checklist as saved,
// so it is refused with the same error Firebase gives when it has no copy at all. The Task tab
// then keeps what is on screen and reads again once the signal is usable (10/05/2026).
export const getDealTasks = async (dealId) => {
  const dealRef = doc(firestored, "deals", dealId);
  const snap = await getDoc(dealRef);
  if (snap.metadata && snap.metadata.fromCache) {
    const err = new Error("Failed to get document because the client is offline.");
    err.code = "unavailable";
    throw err;
  }

  if (snap.exists()) {
    return snap.data().tasks || [];
  }
  return [];
};

export const addTaskToDeal = async (dealId, task) => {
  assertCanWrite();
  const dealRef = doc(firestored, "deals", dealId);

  const snap = await getDoc(dealRef);

  let existingTasks = [];

  if (snap.exists()) {
    existingTasks = snap.data().tasks || [];
  }

  const updatedTasks = [...existingTasks, task];

  retireLaunchRead("deals");
  await setDoc(dealRef, { tasks: updatedTasks }, { merge: true });
};

export const updateDealTasks = async (dealId, tasks) => {
  assertCanWrite();
  const ref = doc(firestored, "deals", dealId);

  retireLaunchRead("deals");
  await setDoc(
    ref,
    { tasks: tasks },
    { merge: true }, // ✅ VERY IMPORTANT
  );
};

// A deal's checklist (deal.tasks) is one array on the deal, and several screens change it: the Task
// tab and its pop-up, the Work tab, a Deals Board drag, Listing Prep's "Set owner". Each used to save
// the whole array from the copy it loaded when it opened, so a tick or a hand-off made a moment
// earlier somewhere else was silently undone. Every checklist change goes through here instead: the
// deal is re-read inside a transaction, `change` is handed the FRESH array and returns the next one,
// and only that is written (the server's task writes work the same way). `change` can run more than
// once if the deal changes mid-save, so it only computes. Handing back the same array writes nothing.
// `change` also gets the whole deal as saved, for changes that depend on its other fields.
// Resolves to the list as saved.
//
// keepUntilConnected (10/05/2026): a transaction cannot run with no connection. Firebase tries for
// about ten seconds and gives up, and Henry's VPN for the MLS breaks every connection when it
// switches, so a tick made just before he left the tab failed and was undone on screen. With this
// option a save that fails for a connection reason is kept and tried again once the signal is back
// (utils/saveWhenConnected.js); each try re-reads the deal, so it still decides against the
// checklist as saved right then. The ticks and the Task tab's own edits ask for it. Stage seeding,
// Turn In and the Settings tools do not: they fail at once as before, and their screens carry on.
export const mutateDealTasks = async (dealId, change, { keepUntilConnected = false } = {}) => {
  assertCanWrite();
  const ref = doc(firestored, "deals", String(dealId));
  let moved = [];
  const run = () =>
    runTransaction(firestored, async (tx) => {
      moved = [];
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Deal not found");
      const fresh = Array.isArray(snap.data().tasks) ? snap.data().tasks : [];
      const next = change(fresh, snap.data());
      if (!Array.isArray(next) || next === fresh) return fresh;
      retireLaunchRead("deals");
      tx.update(ref, { tasks: next });
      moved = jobOwnerChanges(fresh, next);
      return next;
    });
  const saved = await (keepUntilConnected ? saveWhenConnected(run) : run());
  // A task handed to someone else takes its Work job along, whichever screen made the hand-off.
  // After the save, and never a reason for it to fail: the checklist is already saved.
  for (const { jobId, ownerId } of moved) {
    try {
      // launch-copy-safe: a Work job is its own document under the deal, not the deal.
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(doc(firestored, "deals", String(dealId), "work", jobId), { ownerId, updatedAt: Date.now() });
    } catch (e) {
      console.error("Work job owner update failed:", e);
    }
  }
  return saved;
};

// One task, by id: `change` gets that task as it is saved right now and returns its new shape (run
// through cleanDealTask, THE whitelist for deal.tasks). Every other task stays exactly as saved, and
// a task no longer on the deal changes nothing. A `change` that hands the task back as it is writes
// nothing (a change already saved, met again on a second run). `options` go to mutateDealTasks.
export const updateDealTaskById = (dealId, taskId, change, options) =>
  {
  if (isLookOnly()) return lookOnlyRejection();
  return mutateDealTasks(dealId, (tasks) => {
    let changed = false;
    const next = tasks.map((t) => {
      if (String(t.id) !== String(taskId)) return t;
      const out = change(t);
      if (out === t) return t;
      changed = true;
      return cleanDealTask(out);
    });
    return changed ? next : tasks;
  }, options);
};

// One task off the deal, by id; the rest stay exactly as saved.
export const removeDealTask = (dealId, taskId, options) =>
  {
  if (isLookOnly()) return lookOnlyRejection();
  return mutateDealTasks(dealId, (tasks) => {
    const next = tasks.filter((t) => String(t.id) !== String(taskId));
    return next.length === tasks.length ? tasks : next;
  }, options);
};

// Newly seeded tasks (a card moved into a new stage) go after the saved ones. One whose id is already
// on the deal is skipped, and nothing already on the deal is dropped or changed.
export const addDealTasks = (dealId, newTasks, options) =>
  {
  if (isLookOnly()) return lookOnlyRejection();
  return mutateDealTasks(dealId, (tasks) => {
    const key = (t) => String(t?.id ?? t?.createdat ?? t?.title);
    const have = new Set(tasks.map(key));
    const add = (newTasks || []).filter((t) => !have.has(key(t)));
    return add.length ? [...tasks, ...add] : tasks;
  }, options);
};

// Move Them (Settings > Coordinators): hand an agent's still-open items from the old person to
// newPerson (minimal shape, or null). `deals` is the already-loaded list, used only to find which
// deals to open; each deal is then re-read inside a transaction (mutateDealTasks) and only items
// that STILL fit (open, still on the old person) move, so a tick or hand-off made elsewhere since
// the page loaded is kept. Each moved item is stamped handedOff* like a Task tab hand-off.
// Resolves to the number of items moved.
const moveAgentItems = async (deals, matches, fits, newPerson, byName) => {
  const dealIds = [...new Set(matches.map((m) => m.dealId))];
  const byId = new Map((deals || []).map((d) => [String(d.createdat), d]));
  let moved = 0;
  for (const dealId of dealIds) {
    const deal = byId.get(dealId);
    let count = 0;
    await mutateDealTasks(dealId, (fresh) => {
      count = 0;
      const next = fresh.map((t) => {
        if (!fits(t, deal)) return t;
        count++;
        return cleanDealTask({
          ...t,
          assignee: newPerson || null,
          handedOffFrom: t.assignee || null,
          handedOffBy: byName || "",
          handedOffAt: prepTodayYMD(),
        });
      });
      return count ? next : fresh;
    });
    moved += count;
  }
  return moved;
};

// The agent's Listing Coordinator changed: their LC jobs (flagged LC or routedToLC) move.
export const moveAgentCoordinatorTasks = (deals, agentId, oldCoordId, newCoord, byName = "") =>
  moveAgentItems(deals, findAgentCoordinatorTasks(deals, agentId, oldCoordId), (t) => movesWithLC(t, oldCoordId), newCoord, byName);

// The agent's Client Care (Transaction) person changed: their items on live files move, except a
// listing's LC jobs (My Checklist spec 09/27/2026, decision 4).
export const moveAgentClientCareTasks = (deals, agentId, oldId, newPerson, byName = "", today = prepTodayYMD()) =>
  moveAgentItems(deals, findAgentClientCareTasks(deals, agentId, oldId, today), (t, deal) => movesWithClientCare(t, oldId, deal), newPerson, byName);

// One-time backfill: add the template checklist to every ACTIVE, non-archived deal.
// Current stage tasks -> Open, earlier -> Completed; coordinator tasks resolve to
// each deal's agent's coordinator. Idempotent (skips already-tracked stages).
// Returns { updated, skipped }.
export const backfillActiveDeals = async (deals, template, usersById, nowIso) => {
  assertCanWrite();
  let updated = 0;
  let skipped = 0;
  for (const deal of deals || []) {
    if (deal.archived || !ACTIVE_STAGES.includes(deal.dealStage)) {
      skipped++;
      continue;
    }
    // Henry 10/08/2026: the same people every other seeder uses (coordinatorsFor, so a switched-off
    // coordinator is never handed new items), not a hand-rolled lookup.
    const owners = coordinatorsFor(deal.agentData, Object.values(usersById || {}));
    const result = buildDealBackfill(deal, template, { ...owners, nowIso });
    if (!result) {
      skipped++;
      continue;
    }
    retireLaunchRead("deals");
    await updateDoc(doc(firestored, "deals", String(deal.createdat)), {
      tasks: result.tasks,
      taskStatusTracker: result.taskStatusTracker,
    });
    updated++;
  }
  return { updated, skipped };
};

export async function saveNoteToFirebase(dealId, text) {
  assertCanWrite();
  // Use setDoc with merge so this works even if the deal doc somehow lacks
  // a `notes` field or was created out-of-band. updateDoc would throw if the
  // doc doesn't exist; merge:true upserts safely.
  const dealRef = doc(firestored, "deals", String(dealId));
  retireLaunchRead("deals");
  await setDoc(dealRef, { notes: text }, { merge: true });
}

export async function getNoteFromFirebase(dealId) {
  try {
    const dealRef = doc(firestored, "deals", dealId);
    const dealSnap = await getDoc(dealRef);
    if (dealSnap.exists()) {
      return dealSnap.data().notes || ""; // fetch the "notes" key
    } else {
      return "";
    }
  } catch (error) {
    console.error("Error fetching note:", error);
    return "";
  }
}

// A person's notifications, newest first. { max } is the bell's latest-only read
// (2026-09-14, speed wave 5): the bell used to download every notification the person
// had ever received, again on every page. limit() rides the index that already exists
// (firestore.indexes.json: notifications, assigneded + notificationid), so nothing new
// needs deploying. The See All page passes no max and still reads every one.
export const getNotifications = async (userid, { max } = {}) => {
  const parts = [where("assigneded", "==", userid), orderBy("notificationid", "desc")];
  if (max) parts.push(limit(max));
  const snapshot = await getDocs(query(collection(firestored, "notifications"), ...parts));

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

// Every unread notification for a person, however old. The bell's Mark All Read marks
// these too, now that the bell shows only the latest 30. Same query and index as the
// full list, filtered here: a where("read", ...) filter would need a new index and
// would miss notifications saved before the read field existed.
export const getUnreadNotificationIds = async (userid) => {
  const snapshot = await getDocs(
    query(
      collection(firestored, "notifications"),
      where("assigneded", "==", userid),
      orderBy("notificationid", "desc"),
    ),
  );

  return snapshot.docs.filter((d) => d.data()?.read !== true).map((d) => d.id);
};

export const markNotificationAsRead = async (id) => {
  assertCanWrite();
  try {
    const ref = doc(firestored, "notifications", id);
    await updateDoc(ref, {
      read: true,
    });
  } catch (error) {
    console.error("Error updating notification:", error);
  }
};

// Mark a batch of notifications read in one shot (the bell's Mark All Read).
// Chunked at 450 to stay under Firestore's 500-writes-per-batch cap.
export const markAllNotificationsAsRead = async (ids = []) => {
  assertCanWrite();
  try {
    for (let i = 0; i < ids.length; i += 450) {
      const batch = writeBatch(firestored);
      for (const id of ids.slice(i, i + 450)) {
        batch.update(doc(firestored, "notifications", String(id)), { read: true });
      }
      await batch.commit();
    }
    return { success: true };
  } catch (error) {
    console.error("Error marking all notifications read:", error);
    return { success: false, error: error.message };
  }
};

// Two task templates, one per side (Henry, 2026-09-10: buyer files were
// getting the listing checklist). "template" is the listing checklist that
// has always existed; "buyerTemplate" is new and starts empty. Callers that
// pass no side keep getting the listing template, exactly as before.
const TEMPLATE_DOC = "template";
const BUYER_TEMPLATE_DOC = "buyerTemplate";
const templateDocFor = (side) =>
  String(side || "").trim().toLowerCase() === "buyer" ? BUYER_TEMPLATE_DOC : TEMPLATE_DOC;

export const getTaskTemplate = async (side = "listing") => {
  const ref = doc(firestored, "tasktemplate", templateDocFor(side));
  const snap = await getDoc(ref);

  return snap.exists() ? snap.data() : {};
};
export const getTaskTemplates = async () => {
  const [listing, buyer] = await Promise.all([getTaskTemplate("listing"), getTaskTemplate("buyer")]);
  return { listing, buyer };
};
// The template a deal of this side seeds from: the Buyer template once it has
// tasks, else the listing template (the side filters in taskSections still
// strip listing-only stages and "Seller-" rows for a buyer file).
export const getTaskTemplateForSide = async (dealtype) =>
  templateForSide(await getTaskTemplates(), dealtype);

// Listing Prep rollout (spec 2026-09-25, section 11). Assigns every open task on every prep-stage
// listing from the template's rules, once, and stamps the stage. Henry 09/25/2026: existing tasks
// keep no due date (tasksDated stays 0) unless dateExisting is passed, so nobody is chased for old
// rows. Safe to re-run: a deal that needs nothing gets no write. Returns counts for the Settings tool.
export const startListingPrepForAllDeals = async ({ dryRun = false, dateExisting = false } = {}) => {
  assertCanWrite();
  const [deals, users, template] = await Promise.all([getAlldealforAdmin("deals"), getUsers(), getTaskTemplate("listing")]);
  const today = prepTodayYMD();
  let looked = 0;
  let changed = 0;
  let tasksDated = 0;
  let tasksAssigned = 0;
  for (const deal of deals || []) {
    looked += 1;
    const patch = startListingPrepPatch(deal, template || {}, users || [], today, { dateExisting });
    if (!patch) continue;
    changed += 1;
    if (patch.tasks) {
      const before = new Map((deal.tasks || []).map((t) => [String(t.id), t]));
      for (const t of patch.tasks) {
        const b = before.get(String(t.id)) || {};
        if (t.deadline && !b.deadline) tasksDated += 1;
        if (t.assignee?.id && !b.assignee?.id) tasksAssigned += 1;
      }
    }
    if (!dryRun) {
      retireLaunchRead("deals");
      await setDoc(doc(firestored, "deals", String(deal.createdat)), patch, { merge: true });
    }
  }
  return { looked, changed, tasksDated, tasksAssigned };
};

// Start Client Care Checklist (My Checklist spec 09/27/2026, decision 6): one run that gives the open
// current-stage items on buyer files and contract-stage listings an owner and a forward due date, and
// moves auto-routed LC items to Client Care. Never a file the 7 AM chaser can see, so nothing is
// emailed. The dry run counts from the loaded deals; the real run re-reads each deal inside a
// transaction (mutateDealTasks) and counts what it actually wrote. Safe to re-run.
export const startClientCareChecklistForAllDeals = async ({ dryRun = false, byName = "" } = {}) => {
  assertCanWrite();
  const [deals, users, templates, teamDefaults] = await Promise.all([
    getAlldealforAdmin("deals"),
    getUsers(),
    getTaskTemplates(),
    loadTeamDefaults(),
  ]);
  const today = prepTodayYMD();
  const backupId = (teamDefaults && teamDefaults.clientCareOwnerId) || null;
  const totals = { looked: 0, changed: 0, assigned: 0, dated: 0, moved: 0 };
  for (const deal of deals || []) {
    totals.looked += 1;
    const opts = { template: templateForSide(templates, deal.dealtype), users: users || [], today, backupId, byName };
    let result = clientCareStartTasks(deal.tasks, deal, opts);
    if (!result.assigned && !result.dated && !result.moved) continue;
    if (!dryRun) {
      await mutateDealTasks(String(deal.createdat), (fresh) => {
        result = clientCareStartTasks(fresh, deal, opts);
        return result.tasks;
      });
      if (!result.assigned && !result.dated && !result.moved) continue;
    }
    totals.changed += 1;
    totals.assigned += result.assigned;
    totals.dated += result.dated;
    totals.moved += result.moved;
  }
  return totals;
};

// Listing Prep page "Set owner" (spec 2026-09-25, section 3): the agent gets a Listing Coordinator
// on their profile (so it sticks for the next listing too) and this listing's open, unassigned
// tasks go to that person now.
export const setAgentListingCoordinator = async (agentId, coordinatorId, deal, users) => {
  assertCanWrite();
  await updateDoc(doc(firestored, "users", String(agentId)), { listingCoordinatorId: String(coordinatorId) });
  const c = (users || []).find((u) => u.id === coordinatorId);
  if (!deal || !c) return;
  if (!Array.isArray(deal.tasks) || deal.tasks.length === 0) return;
  const snapshot = { id: c.id, firstname: c.firstname || "", lastname: c.lastname || "", profilepicture: c.profilepicture || "", role: c.role || "" };
  // Decided against the checklist as saved now, never the page's copy: a task ticked or handed to
  // someone since the page loaded keeps that.
  await mutateDealTasks(String(deal.createdat), (fresh) =>
    fresh.map((t) => cleanDealTask(isOpenTask(t) && !(t.assignee && t.assignee.id) ? { ...t, assignee: snapshot, routedToLC: true } : t)),
  );
};

export const saveTaskTemplate = async (data, side = "listing") => {
  assertCanWrite();
  const ref = doc(firestored, "tasktemplate", templateDocFor(side));
  await setDoc(ref, data);
};

export const addElectronicLockbox = async (data) => {
  assertCanWrite();
  const docRef = doc(firestored, "electronic_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updateElectronicLockbox = async (id, data) => {
  assertCanWrite();
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "electronic_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const getElectronicLockboxes = async () => {
  const snap = await getDocs(collection(firestored, "electronic_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const deleteElectronicLockbox = async (id) => {
  assertCanWrite();
  try {
    const docRef = doc(firestored, "electronic_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting electronic lockbox:", error);
    throw error;
  }
};

export const addPushButtonLockbox = async (data) => {
  assertCanWrite();
  const docRef = doc(firestored, "pushbutton_lockboxes", data.id);

  await setDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const updatePushButtonLockbox = async (id, data) => {
  assertCanWrite();
  if (!id) {
    throw new Error("Document ID is required for update.");
  }

  const docRef = doc(firestored, "pushbutton_lockboxes", id);

  await updateDoc(docRef, {
    ...data,
  });

  return docRef;
};

export const deletePushButtonLockbox = async (id) => {
  assertCanWrite();
  try {
    const docRef = doc(firestored, "pushbutton_lockboxes", id);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting pushbutton lockbox:", error);
    throw error;
  }
};

export const getPushButtonLockbox = async () => {
  const snap = await getDocs(collection(firestored, "pushbutton_lockboxes"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addContactToFirebase = async (data) => {
  assertCanWrite();
  const docRef = doc(firestored, "sync_contacts", String(data.id));

  await setDoc(docRef, data);

  // eslint-disable-next-line no-use-before-define -- verified safe 2026-09-11: deferred reference, runs after the declaration
  invalidateContactsCache();
  forgetContacts([data?.id]);
  return docRef;
};

// Per-user contacts cache. An agent can have thousands of contacts, and the
// full list was being re-fetched on every screen/deal-detail open (slow, and it
// caused a spurious auto-save when the late load changed component state). Cache
// the list briefly and reuse it; any contact write clears the cache so changes
// show immediately. Short TTL bounds staleness even if a write path is missed.
const _contactsCache = new Map(); // userId -> { at: number, data: [] }
const CONTACTS_TTL_MS = 60 * 1000;

// Synchronous read of cached contacts (null if absent/stale). Lets callers seed
// initial state without an async round-trip on a warm cache.
export const getCachedContacts = (createdAtId) => {
  const hit = _contactsCache.get(createdAtId);
  if (hit && Date.now() - hit.at < CONTACTS_TTL_MS) return hit.data;
  return null;
};

// Reserved cache key for the team-wide list (never a real user id).
export const TEAM_CONTACTS_CACHE_KEY = "__team__";

// Clear the cache (one user, or all). Call after any contact create/update/delete.
// Clearing one user also clears the team-wide list — it contains that user's rows.
export const invalidateContactsCache = (createdAtId) => {
  if (createdAtId == null) _contactsCache.clear();
  else {
    _contactsCache.delete(createdAtId);
    _contactsCache.delete(TEAM_CONTACTS_CACHE_KEY);
  }
};

// GET all synced contacts (cached for CONTACTS_TTL_MS per user)
export const getAllContactsFromFirebase = async (createdAtId) => {
  const cached = getCachedContacts(createdAtId);
  if (cached) return cached;

  const q = query(
    collection(firestored, "sync_contacts"),
    where("createdBy", "==", createdAtId),
  );

  const snapshot = await getDocs(q);

  // Archived contacts (contactArchive.js) stay out of every list. Lookups by id
  // (getContactsByIds) still find them, so old records never show a blank client.
  const data = withoutArchived(
    snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })),
  );

  _contactsCache.set(createdAtId, { at: Date.now(), data });
  return data;
};

// GET every contact in the team account (no createdBy filter). Cached under
// TEAM_CONTACTS_CACHE_KEY — NEVER under a user id, so the per-user Contacts
// page cache can't be poisoned with other agents' rows.
export const getTeamContactsFromFirebase = async () => {
  const cached = getCachedContacts(TEAM_CONTACTS_CACHE_KEY);
  if (cached) return cached;

  const snapshot = await getDocs(collection(firestored, "sync_contacts"));
  // Archived contacts stay out of the pickers too (same rule as above).
  const data = withoutArchived(
    snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })),
  );

  _contactsCache.set(TEAM_CONTACTS_CACHE_KEY, { at: Date.now(), data });
  return data;
};

// Role-aware contact list for form lookups. Eligible staff (see
// canSearchTeamContacts) get the whole team's contacts, with rows they don't
// own tagged `_teamOwnerName` for display. Everyone else — and any team-fetch
// failure — gets today's per-user list. "My book" views (Contacts page,
// texting, FUB import) must keep calling getAllContactsFromFirebase directly.
export const getContactsForUser = async (user) => {
  if (user && canSearchTeamContacts(user)) {
    try {
      const [contacts, users] = await Promise.all([
        getTeamContactsFromFirebase(),
        getUsers(),
      ]);
      return tagTeamContacts(contacts, users, user.id);
    } catch (err) {
      console.error("Team contacts load failed; using own contacts:", err);
    }
  }
  return getAllContactsFromFirebase(user?.id);
};

export const updateContactInFirebase = async (id, data) => {
  assertCanWrite();
  const ref = doc(firestored, "sync_contacts", id);
  // Accept arbitrary fields (name/email/phone/notes/etc.) so callers can patch
  // a single field without clobbering the others.
  await updateDoc(ref, { ...data });
  invalidateContactsCache();
  // Screens showing this client's name read it fresh (utils/contactCache.js).
  forgetContacts([id]);
};

// ============ CONTACT REFERRALS ============
// The referral link lives in ONE place: on the referred person's contact doc
// (referredByContactId + denormalized referredByName). Written as a direct
// Firestore patch — the /update-contact backend allowlist drops these fields
// from its own payloads, but every backend write is merge:true, so a direct
// patch survives all Google/FUB sync paths. Do NOT route through updateContact.
export const setContactReferrer = async (contactId, referrer) => {
  try {
    await updateContactInFirebase(String(contactId), {
      referredByContactId: referrer ? String(referrer.contactId) : "",
      referredByName: referrer ? referrer.name || "" : "",
    });
    return { success: true };
  } catch (error) {
    console.error("Error setting referrer:", error);
    return { success: false, error: error.message };
  }
};

// Everyone this contact referred (the "down" direction of the referral tree).
export const listReferredContacts = async (contactId) => {
  try {
    const snap = await getDocs(
      query(
        collection(firestored, "sync_contacts"),
        where("referredByContactId", "==", String(contactId)),
      ),
    );
    return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
  } catch (error) {
    console.error("Error listing referred contacts:", error);
    return [];
  }
};

export const deleteContactFromFirebase = async (id) => {
  assertCanWrite();
  try {
    if (!id) throw new Error("Contact ID is required");

    const docRef = doc(firestored, "sync_contacts", id);
    await deleteDoc(docRef);

    invalidateContactsCache();
    forgetContacts([id]);
    return true;
  } catch (error) {
    console.error("Delete Error:", error);
    throw error;
  }
};

export const saveContactType = async (data) => {
  assertCanWrite();
  try {
    const docRef = doc(collection(firestored, "contact_type"));

    await setDoc(docRef, {
      id: docRef.id,
      abbreviation: data.abbreviation,
      type: data.type,
      clientType: data.selectedOption,
      createdAt: data.userId,
    });

    return { success: true, id: docRef.id }; // ✅ success only after Firestore commit
  } catch (error) {
    console.error("Error saving contact type:", error);
    return { success: false, error: error.message };
  }
};

export const updateContactType = async (id, data) => {
  assertCanWrite();
  try {
    // Fetch existing document data
    const docRef = doc(firestored, "contact_type", id);

    await updateDoc(docRef, data);
    return { success: true, id: id };
  } catch (error) {
    console.error("Error updating coontact type:", error);
    return { success: false, error: error.message };
  }
};

export const listenContactType = (callback) => {
  const q = query(
    collection(firestored, "contact_type"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const sortedData = [...data].sort((a, b) =>
      (a.abbreviation || "").localeCompare(b.abbreviation || "", undefined, {
        sensitivity: "base",
      }),
    );
    callback(sortedData);
  });

  return unsubscribe;
};

export const deleteContactType = async (id) => {
  assertCanWrite();
  try {
    await deleteDoc(doc(firestored, "contact_type", id)); // ✅ wait for Firestore delete
    return { success: true };
  } catch (error) {
    console.error("Error deleting contact type:", error);
    return { success: false, error: error.message };
  }
};

export const getContactTypes = async () => {
  try {
    const snapshot = await getDocs(collection(firestored, "contact_type"));

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching contact types:", error);
    return [];
  }
};

export const updateDealById = async (dealId, updatedData) => {
  assertCanWrite();
  try {
    const dealRef = doc(firestored, "deals", String(dealId));

    retireLaunchRead("deals");
    await setDoc(dealRef, updatedData, { merge: true });

    return true;
  } catch (error) {
    console.error("Error updating deal:", error);
    throw error;
  }
};

// Listing Prep (spec 2026-09-25, section 4): when one of a listing's anchor dates changes, every
// open before-date or after-date task that nobody moved by hand follows it. The date patch is a plain
// field write; the re-dating then goes through mutateDealTasks, so a tick made elsewhere a moment
// earlier is never written over, and nothing is written when no date moves. A patch that touches no
// anchor is exactly updateDealById.
export const updateDealAndRedateTasks = async (dealId, patch) => {
  assertCanWrite();
  const touches = DUE_ANCHORS.some((a) => Object.prototype.hasOwnProperty.call(patch || {}, a.field));
  if (!touches) return updateDealById(dealId, patch);
  await updateDealById(dealId, patch);
  const today = prepTodayYMD();
  await mutateDealTasks(dealId, (fresh, saved) => {
    const next = recomputeAnchoredDeadlines(fresh, { ...(saved || {}), ...patch }, today);
    const moved = next.some((t, i) => (t && t.deadline) !== (fresh[i] && fresh[i].deadline));
    return moved ? next : fresh;
  });
  return true;
};

export const updatePushButtonLockboxById = async (id, data) => {
  assertCanWrite();
  const ref = doc(firestored, "pushbutton_lockboxes", id);
  await updateDoc(ref, data);
};

export const updateElectronicLockboxById = async (id, data) => {
  assertCanWrite();
  const ref = doc(firestored, "electronic_lockboxes", id);
  await updateDoc(ref, data);
};

// Hard-delete a user document (no soft delete / type flag — gone from Firestore).
export const hardDeleteUser = async (id) => {
  assertCanWrite();
  if (!id) return false;
  await deleteDoc(doc(firestored, "users", String(id)));
  return true;
};

// Unassign any lockbox (electronic or push-button) whose assignedDealId matches.
// Used when we detect a stale reference (e.g., the deal was deleted).
// Firestore == is type-strict, so we try the dealId as both string and number
// to cover historical data shapes.
export const unassignLockboxesByDealId = async (dealId) => {
  assertCanWrite();
  if (!dealId) return 0;
  let count = 0;
  const clear = {
    status: "Available",
    assignedDealId: "",
    assignedDealLabel: "",
  };
  const candidates = [];
  const asNumber = Number(dealId);
  if (!Number.isNaN(asNumber)) candidates.push(asNumber);
  candidates.push(String(dealId));
  const seenIds = new Set();
  for (const collectionName of [
    "electronic_lockboxes",
    "pushbutton_lockboxes",
  ]) {
    for (const value of candidates) {
      try {
        const snap = await getDocs(
          query(
            collection(firestored, collectionName),
            where("assignedDealId", "==", value),
          ),
        );
        for (const d of snap.docs) {
          if (seenIds.has(d.ref.path)) continue;
          seenIds.add(d.ref.path);
          await updateDoc(d.ref, clear);
          count += 1;
        }
      } catch (err) {
        console.error(
          `Failed cleaning ${collectionName} for value ${value}:`,
          err,
        );
      }
    }
  }
  return count;
};

// Firestore's limit on how many values one "in" query may list.
const CONTACT_BATCH_SIZE = 30;

export const getContactsByIds = async (contactIds = []) => {
  // Drop null/empty IDs up front — an empty doc path makes Firestore throw. An ID
  // with a slash can never name a contact document, and would make the whole batch
  // query below throw, so it is skipped too (it always came back empty before).
  const ids = (contactIds || [])
    .filter((id) => id != null && String(id).trim() !== "")
    .map((id) => String(id).trim())
    .filter((id) => !id.includes("/"));
  if (!ids.length) return [];

  const toContact = (snapshot) => ({
    ...snapshot.data(),
    id: snapshot.id,
    firebaseId: snapshot.id,
  });
  const byId = new Map();

  // Up to 30 contacts per query instead of one request per contact, so a few
  // hundred clients are a handful of queries (2026-09-14, speed wave 3). If a batch
  // fails, its contacts are read one at a time: one bad or missing ID still
  // resolves to nothing instead of blanking out every other name.
  const unique = [...new Set(ids)];
  const chunks = [];
  for (let i = 0; i < unique.length; i += CONTACT_BATCH_SIZE) {
    chunks.push(unique.slice(i, i + CONTACT_BATCH_SIZE));
  }
  await Promise.all(
    chunks.map(async (chunk) => {
      try {
        const snap = await getDocs(
          query(
            collection(firestored, "sync_contacts"),
            where(documentId(), "in", chunk),
          ),
        );
        snap.docs.forEach((d) => byId.set(d.id, toContact(d)));
      } catch (batchError) {
        console.error("Batch contact lookup failed, reading one at a time:", batchError);
        await Promise.all(
          chunk.map(async (contactId) => {
            try {
              const snapshot = await getDoc(doc(firestored, "sync_contacts", contactId));
              if (snapshot.exists()) byId.set(snapshot.id, toContact(snapshot));
            } catch (error) {
              console.error("Error fetching contact by ID:", contactId, error);
            }
          }),
        );
      }
    }),
  );

  // Same shape and order as before: one entry per ID asked for, missing ones left out.
  return ids.map((id) => byId.get(id)).filter(Boolean);
};

// ==== Team Directory (shared professionals in the team Google account) ====
//
// "Google is the master": pros live as real Google Contacts in one shared
// account; deal addresses are appended to each contact's Notes. The backend
// Cloud Function (functions-api) holds the shared account's credentials and
// does the Google People API work — the client just calls these endpoints.

const TEAM_DIRECTORY_API =
  "https://us-central1-team-edelstein.cloudfunctions.net/api";

// List the shared account's contacts for the Team Directory page.
export const getTeamDirectory = async () => {
  try {
    const res = await authedFetch(`${TEAM_DIRECTORY_API}/team-directory`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.contacts || [];
  } catch (error) {
    console.error("Error loading team directory:", error);
    return [];
  }
};

// Manually add a professional to the shared Team Directory. Rides the same
// backend upsert the deal-save sync uses (matches by email, then name+phone,
// so re-adding someone never duplicates them). Requires a name plus a phone
// or an email.
export const addToTeamDirectory = async ({
  name,
  role = "",
  company = "",
  phone = "",
  email = "",
  person = null, // optional FULL rich contact — the directory entry then carries everything
}) => {
  assertCanWrite();
  const res = await authedFetch(`${TEAM_DIRECTORY_API}/team-directory/log`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      address: "Added to Directory",
      date: Date.now(),
      pros: [{ name, role, company, phone, email, person: person || undefined }],
    }),
  });
  if (!res.ok) throw new Error("Directory save failed");
  return res.json();
};

// Upsert each professional participant on a deal into the shared Google account
// and append this property address to their Notes. Fire-and-forget; the backend
// matches (email → name+phone) and is idempotent (won't duplicate a line).
export const syncParticipantsToDirectory = async (
  participants,
  { address } = {},
) => {
  try {
    const pros = (participants || []).map(normalizePro).filter(isLoggablePro);
    if (!pros.length) return;
    await authedFetch(`${TEAM_DIRECTORY_API}/team-directory/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, date: Date.now(), pros }),
    });
  } catch (error) {
    console.error("Team Directory sync failed:", error);
  }
};

// ============ LISTING APPOINTMENTS ============
// Each document represents one listing appointment that an agent went on
// (or has scheduled). Outcome is tracked via the `status` field.

export const getListingAppointments = async () => {
  const snap = await getDocs(query(collection(firestored, "listingAppointments")));
  return snap.docs.map((d) => {
    const a = { id: d.id, ...d.data() };
    a.address = stripCountry(a.address);
    return a;
  });
};

export const saveListingAppointment = async (data) => {
  assertCanWrite();
  const id =
    data.id ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await setDoc(
    doc(firestored, "listingAppointments", id),
    { ...data, id, createdAt: data.createdAt || new Date().toISOString() },
    { merge: true },
  );
  return id;
};

export const updateListingAppointment = async (id, updates) => {
  assertCanWrite();
  await setDoc(
    doc(firestored, "listingAppointments", id),
    updates,
    { merge: true },
  );
};

export const deleteListingAppointment = async (id) => {
  assertCanWrite();
  await deleteDoc(doc(firestored, "listingAppointments", id));
};

// When a listing lead's fate is decided, its logged appointment classifies
// itself — graduated to the Deals Board → "won", marked lost → "lost" — so
// the Goals log stays current without anyone updating it by hand (Henry,
// 2026-08-30). Only a PENDING appointment flips: a status a person picked is
// never overwritten, and re-runs can't clobber anything. A miss is silent —
// plenty of leads never had a logged appointment. Never throws; the lead
// write this rides on must not fail because of the log.
export const classifyAppointmentForLead = async (lead, status) => {
  try {
    const appts = await getListingAppointments();
    const pending = (appts || []).filter(
      (a) => !a.status || a.status === "pending",
    );
    const match = findAppointmentForLead(pending, lead);
    if (!match) return { classified: false };
    await updateListingAppointment(match.id, {
      status,
      classifiedBy: "pipeline-sync",
    });
    return { classified: true, appointmentId: match.id };
  } catch (e) {
    console.error("Appointment auto-classify failed (the lead is fine):", e);
    return { classified: false, error: e.message };
  }
};

// ============ TEAM FEED POSTS ============
// Posts are stored in the `posts` collection. Reactions are a sparse map
// of userId → "like" | "dislike". reactionCounts are denormalized so the
// feed can render counts without iterating reactions.
// Note: computeNewReactionState is imported from ./feedHelpers at the top of this file.

const POSTS_COLLECTION = "posts";
const POSTS_PAGE_SIZE = 20;

// Caller can pass an explicit `id` (so callers can upload images to a known
// storage path before the post doc exists). If omitted, one is generated.
export async function createPost({
  id,
  authorId,
  authorName,
  authorRole,
  text,
  imageUrls = [],
}) {
  assertCanWrite();
  const postId = id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const post = {
    id: postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    imageUrls: Array.isArray(imageUrls) ? imageUrls : [],
    createdAt: Date.now(),
    updatedAt: null,
    reactions: {},
    reactionCounts: { ...EMPTY_REACTION_COUNTS },
    commentCount: 0,
    deleted: false,
  };
  await setDoc(doc(firestored, POSTS_COLLECTION, postId), post);
  return post;
}

export function generatePostId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Cursor is `{ createdAt, id }` or null. The `id` tiebreaker prevents skipping
// posts when two share the same createdAt timestamp (e.g., bulk imports).
export async function getPosts({ cursor = null, pageSize = POSTS_PAGE_SIZE } = {}) {
  const constraints = [
    where("deleted", "==", false),
    orderBy("createdAt", "desc"),
    orderBy("id", "asc"),
    limit(pageSize + 1),
  ];
  if (cursor !== null) {
    constraints.splice(3, 0, startAfter(cursor.createdAt, cursor.id));
  }
  const q = query(collection(firestored, POSTS_COLLECTION), ...constraints);
  const snap = await getDocs(q);
  const docs = snap.docs.map((d) => d.data());
  const hasMore = docs.length > pageSize;
  const posts = hasMore ? docs.slice(0, pageSize) : docs;
  const last = posts.length > 0 ? posts[posts.length - 1] : null;
  const nextCursor = last ? { createdAt: last.createdAt, id: last.id } : null;
  return { posts, nextCursor, hasMore };
}

// Count posts created strictly after `timestamp` (ms epoch). Uses an aggregate
// count query so we don't download the documents — cheap enough to poll.
export async function countPostsSince(timestamp) {
  const ts = Number(timestamp) || 0;
  const q = query(
    collection(firestored, POSTS_COLLECTION),
    where("deleted", "==", false),
    where("createdAt", ">", ts),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// Stamp "now" as the last time this user opened the Feed, so the unread badge
// resets. Stored on the user doc → consistent across the user's devices.
export async function markFeedSeen(userId) {
  if (!userId) return;
  await updateUserProfile(userId, { lastSeenFeedAt: Date.now() });
}

export async function updatePost(id, updates) {
  assertCanWrite();
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), {
    ...updates,
    updatedAt: Date.now(),
  });
}

// Soft-delete: feed queries filter out deleted=true.
export async function deletePost(id) {
  assertCanWrite();
  await updateDoc(doc(firestored, POSTS_COLLECTION, id), { deleted: true });
}

// Atomic toggle so two simultaneous clicks don't corrupt counts.
// Returns the updated post.
export async function togglePostReaction(postId, userId, reaction) {
  assertCanWrite();
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  return await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(postRef);
    if (!snap.exists()) throw new Error("Post not found");
    const post = snap.data();
    const prev = post.reactions?.[userId] || null;
    const { newReaction, deltas } = computeNewReactionState(prev, reaction);
    const newReactions = { ...(post.reactions || {}) };
    if (newReaction === null) {
      delete newReactions[userId];
    } else {
      newReactions[userId] = newReaction;
    }
    const newCounts = applyReactionDeltas(post.reactionCounts, deltas);
    tx.update(postRef, {
      reactions: newReactions,
      reactionCounts: newCounts,
    });
    return {
      ...post,
      reactions: newReactions,
      reactionCounts: newCounts,
    };
  });
}

// Upload a single image file for a feed post. Stored under the same
// dealPhotos/ path that property photo uploads use — that path is already
// covered by the deployed storage rules, so this works without needing to
// ship a new `posts/` rule.
export async function uploadPostImage(file, postId) {
  assertCanWrite();
  if (!file) throw new Error("No file provided");
  const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
  const path = `dealPhotos/feed/${postId}/${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
}

// ============ TEAM FEED COMMENTS ============
// Comments live in posts/{postId}/comments sub-collection. The parent post's
// commentCount is kept in sync via a transaction.

export async function getCommentsForPost(postId) {
  const q = query(
    collection(firestored, POSTS_COLLECTION, postId, "comments"),
    where("deleted", "==", false),
    orderBy("createdAt", "asc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export async function addComment(postId, { authorId, authorName, authorRole, text }) {
  assertCanWrite();
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const comment = {
    id,
    postId,
    authorId,
    authorName: authorName || "",
    authorRole: authorRole || "",
    text: text || "",
    createdAt: Date.now(),
    updatedAt: null,
    deleted: false,
  };
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", id);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const postSnap = await tx.get(postRef);
    if (!postSnap.exists()) throw new Error("Post not found");
    const post = postSnap.data();
    tx.set(commentRef, comment);
    tx.update(postRef, { commentCount: (post.commentCount || 0) + 1 });
  });
  return comment;
}

export async function updateComment(postId, commentId, updates) {
  assertCanWrite();
  await updateDoc(
    doc(firestored, POSTS_COLLECTION, postId, "comments", commentId),
    { ...updates, updatedAt: Date.now() },
  );
}

// Soft-delete; decrement commentCount on the parent post.
export async function deleteComment(postId, commentId) {
  assertCanWrite();
  const commentRef = doc(firestored, POSTS_COLLECTION, postId, "comments", commentId);
  const postRef = doc(firestored, POSTS_COLLECTION, postId);
  await runTransaction(firestored, async (tx) => {
    const commentSnap = await tx.get(commentRef);
    if (!commentSnap.exists()) return;
    const comment = commentSnap.data();
    if (comment.deleted) return;
    const postSnap = await tx.get(postRef);
    tx.update(commentRef, { deleted: true });
    if (postSnap.exists()) {
      const post = postSnap.data();
      tx.update(postRef, {
        commentCount: Math.max(0, (post.commentCount || 0) - 1),
      });
    }
  });
}

// ==== Open Houses ====

// Resolves the first customer on a deal into { name, email }. Handles both:
//   - modern shape: customers = ["contactId", ...] (looked up in sync_contacts)
//   - legacy shape: customers = [{ clientName, name?, email?, ... }, ...]
async function resolveSellerFromDeal(deal) {
  const first = deal?.customers?.[0];
  if (!first) return { name: "", email: "" };
  if (typeof first === "object") {
    return {
      name: first.name || first.clientName || "",
      email: first.email || first.clientEmail || "",
    };
  }
  const contacts = await getContactsByIds([first]);
  const c = contacts[0];
  return { name: c?.name || "", email: c?.email || "" };
}

// Seller Provided Door Code (Henry, 2026-09-18) and the listing's lockboxes (Henry, 2026-09-25):
// the listing as the database holds it now, not as the `deal` handed in says. The deal page's Open
// Houses tab passes the page's own copy of the listing, read once when the page opened, and the
// Listing Details auto-save never refreshes that copy. So a code typed on Overview a minute ago
// would be missed, one the seller asked us to stop sharing would be copied back and emailed to the
// host, and a second lockbox added since would not reach the host at all.
// A failed read falls back to the passed deal: Up For Grabs calls this after its claim is
// already committed, so the open house must still be created.
async function freshListingFor(deal) {
  const id = deal?.createdat ?? deal?.id;
  if (id == null || id === "") return deal || {};
  try {
    const snap = await getDoc(doc(firestored, "deals", String(id)));
    return snap.exists() ? snap.data() : deal || {};
  } catch (err) {
    console.error("createOpenHouse: could not read the listing:", err);
    return deal || {};
  }
}

export const createOpenHouse = async ({ deal, fields, userId }) => {
  assertCanWrite();
  const ref = doc(collection(firestored, "openHouses"));
  const seller = await resolveSellerFromDeal(deal);
  // One read of the listing serves both the door code and the lockboxes (freshListingFor above).
  const listing = await freshListingFor(deal);
  const lockboxes = await resolveLockboxSnapshots(dealLockboxes(listing));
  const firstLockbox = lockboxes[0] || { type: "", number: "", code: "" };
  const sellerDoorCode = doorCodeOf(listing);
  const payload = {
    id: ref.id,
    dealId: deal.id,
    propertyAddress: deal.propertyaddress || "",
    // Denormalized listing agent so the open-house list can show an agent the
    // open houses on a listing they own, without a second deal lookup.
    listingAgentId: deal.agentData || "",
    listingAgentName: deal.agentName || "",
    sellerName: seller.name,
    sellerEmail: seller.email,
    date: fields.date,
    startTime: fields.startTime,
    endTime: fields.endTime,
    hostingAgentId: fields.hostingAgentId || "",
    // Decision 10: true only when the host is a team user (has an id). An outside host never gets the
    // lockbox code or number in an email (openHouseHostAccess.hostIsOutside reads this).
    hostIsTeamMember: !!String(fields.hostingAgentId || "").trim(),
    hostingAgentName: fields.hostingAgentName,
    hostingAgentEmail: fields.hostingAgentEmail,
    notes: fields.notes || "",
    // Denormalized lockbox info so the hosting-agent email can show how to get into the property
    // without a second deal lookup. Henry, 2026-09-25: a listing holds one Push Button AND one
    // Electronic, so the whole list is snapshotted with each box's details and its "Where It Is"
    // note, and the three old fields below stay in step with the FIRST box for every screen and
    // Cloud Function that has not learned the list. An upcoming open house is refreshed when the
    // listing's boxes change (sellerDoorCodeStore.refreshUpcomingOpenHouseLockboxes).
    lockboxes,
    lockboxType: firstLockbox.type,
    lockboxNumber: firstLockbox.number,
    lockboxCode: firstLockbox.code,
    // Seller Provided Door Code (Henry, 2026-09-18): copied the same way, so the
    // scheduling email, Resend Invite and the occupied-home release send the door
    // code instead of the lockbox. "" when the listing has none. Read fresh from
    // the listing (freshListingFor above).
    sellerDoorCode,
    status: "Scheduled",
    // True when the home is occupied and we're holding the scheduling
    // notifications until the user confirms the seller verbally OK'd the
    // time. False (or absent) means notifications can / have gone out.
    pendingSellerApproval: !!fields.pendingSellerApproval,
    feedbackToken: generateFeedbackToken(),
    feedback: null,
    pendingRecap: false,
    recapSentAt: null,
    recapSentBy: null,
    cancelReason: null,
    cancelledAt: null,
    cancelledBy: null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return { id: ref.id };
};

export const updateOpenHouse = async (id, patch, userId) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

// Undo (within UNDO_WINDOW_MS of Mark Complete) and a Team Lead's later Reopen
// are the same write: put the status back to "Scheduled". Nothing else is
// written and nothing is "restored" — deriveStatus treats a missing status and
// "Scheduled" identically, so an undone open house reads as untouched. It goes
// through updateOpenHouse so updatedAt/updatedBy are stamped the way every
// other open-house edit stamps them (the completion's own stamps are
// overwritten and cannot be put back; nothing reads them).
// Two things this deliberately does NOT do:
//   • It cannot take back a completion that came from saving feedback. Feedback
//     on file still reads as Completed, and those paths email the listing agent
//     and the owner the instant they are saved (Henry, 09/16/2026: out of scope).
//   • It re-arms the hosting agent's feedback reminder emails, which is correct:
//     the open house really is awaiting feedback again.
export const reopenOpenHouse = async (id, userId) => {
  await updateOpenHouse(id, { status: "Scheduled" }, userId);
};

export const cancelOpenHouse = async (id, reason, userId) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    status: "Cancelled",
    cancelReason: reason || null,
    cancelledAt: serverTimestamp(),
    cancelledBy: userId,
    updatedAt: serverTimestamp(),
  });
};

export const submitOpenHouseFeedbackInApp = async (id, feedback, userId) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouses", id);
  await updateDoc(ref, {
    feedback: {
      ...feedback,
      submittedAt: serverTimestamp(),
      submittedVia: "app",
      submittedByUserId: userId,
    },
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

export const deleteOpenHouse = async (id) => {
  assertCanWrite();
  await deleteDoc(doc(firestored, "openHouses", id));
};

// Distinct outside hosting agents (not team members) seen on past open
// houses. Used to autocomplete the ScheduleModal name field so the team
// doesn't re-type the same showing agent every time. Returns
// [{ name, email, lastUsedAt }] sorted most-recent first.
export const getOutsideHostingAgents = async () => {
  try {
    const snap = await getDocs(collection(firestored, "openHouses"));
    const seen = new Map(); // key: email lowercased → { name, email, lastUsedAt }
    snap.forEach((d) => {
      const oh = d.data() || {};
      if (oh.hostingAgentId) return; // team member, not outside
      const email = String(oh.hostingAgentEmail || "").trim();
      const name = String(oh.hostingAgentName || "").trim();
      if (!email || !name) return;
      const key = email.toLowerCase();
      const ts = oh.createdAt?.toDate
        ? oh.createdAt.toDate().getTime()
        : (typeof oh.createdAt === "number" ? oh.createdAt : 0);
      const existing = seen.get(key);
      if (!existing || ts > existing.lastUsedAt) {
        seen.set(key, { name, email, lastUsedAt: ts });
      }
    });
    return Array.from(seen.values()).sort((a, b) => b.lastUsedAt - a.lastUsedAt);
  } catch (err) {
    console.error("getOutsideHostingAgents failed:", err);
    return [];
  }
};

export const subscribeOpenHouses = (callback) => {
  const q = query(collection(firestored, "openHouses"));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeOpenHousesForDeal = (dealId, callback) => {
  const q = query(collection(firestored, "openHouses"), where("dealId", "==", dealId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
};

// ---- Open house hosting requests (public hosting page + Requests tab; 09/16/2026) ----
// openHouseRequests/{id}: one agent asking for one home on one date. The server creates
// them (POST /oh-host/request in functions-api) and records seller decisions; the
// browser reads them and marks them sent to the seller. Timestamps here are epoch
// milliseconds, never Firestore Timestamps, so browser, server and tests compare alike.

// Expiry is derived at read time and never stored (a request nobody acts on stays
// "requested" forever), so both open-ended listeners carry a date floor: the set every
// signed-in tab downloads cannot grow without bound. The Requests tab shows the last
// 90 days of requests (every state) plus everything ahead; older rows stay in
// Firestore for the deal page's per-deal listener and the server.
export const REQUESTS_TAB_DAYS_BACK = 90;
const requestsDateFloorIso = () =>
  todayIso(new Date(Date.now() - REQUESTS_TAB_DAYS_BACK * 24 * 60 * 60 * 1000));

export const subscribeOpenHouseRequests = (callback) => {
  const q = query(
    collection(firestored, "openHouseRequests"),
    where("date", ">=", requestsDateFloorIso()),
  );
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

export const subscribeOpenHouseRequestsForDeal = (dealId, callback) => {
  const q = query(
    collection(firestored, "openHouseRequests"),
    where("dealId", "==", String(dealId)),
  );
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

// Delete on the Requests tab (Henry 09/18/2026: "we should be able to delete these as
// well if we want"). Removes the request document only; the tab cancels a linked open
// house first when asked. A request sent to the seller loses its email buttons with it
// (the server finds no request for the token).
export const deleteOpenHouseRequest = async (id) => {
  assertCanWrite();
  const key = String(id || "").trim();
  if (!key) throw new Error("deleteOpenHouseRequest: a request id is required");
  await deleteDoc(doc(firestored, "openHouseRequests", key));
};

// The team fixing the time an agent asked for (Henry 09/25/2026: "if an agent requests
// a time and we need to edit it, we should be able to edit it from within the request
// pop-up window"). Three things go down together:
//   - the corrected date and window, so the seller's email and the hosting agent's
//     email both carry the time we actually mean;
//   - timeChange, the trail: who moved it, when, and the window the AGENT asked for.
//     A second edit keeps the first one, because what Dana asked for is what Dana asked
//     for, and the emails lean on it to avoid reading as though she picked this slot;
//   - status back to "requested" with sellerAsk cleared, for the same reason the server
//     does it in replacementPatch: a decision link the seller is already holding must
//     never approve a time they were never shown. Send to Seller mints a fresh token.
// `request` is the row in hand (its id and its current window); `user` is the clicker.
// Returns the patch that was written, so the popup can show the new time at once, or
// null when the window handed in is the one the request already had (nothing written,
// so an idle Save never drags a sent request back to New).
export const updateOpenHouseRequestTime = async (request, edited, user) => {
  assertCanWrite();
  const r = request || {};
  const key = String(r.id || "").trim();
  if (!key) throw new Error("updateOpenHouseRequestTime: a request id is required");
  const check = validateRequestWindow(edited);
  if (!check.ok) throw new Error(`updateOpenHouseRequestTime: ${check.error}`);
  const date = String(edited.date);
  const startTime = String(edited.startTime);
  const endTime = String(edited.endTime);
  if (date === String(r.date || "") && startTime === String(r.startTime || "") && endTime === String(r.endTime || "")) {
    return null;
  }
  const already = r.timeChange && typeof r.timeChange === "object" ? r.timeChange : null;
  const now = Date.now();
  const patch = {
    date,
    startTime,
    endTime,
    status: "requested",
    sellerAsk: null,
    updatedAt: now,
    timeChange: {
      at: now,
      by: user?.id || "",
      byName: [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.displayname || "",
      fromDate: String(already?.fromDate || r.date || ""),
      fromStartTime: String(already?.fromStartTime || r.startTime || ""),
      fromEndTime: String(already?.fromEndTime || r.endTime || ""),
    },
  };
  await updateDoc(doc(firestored, "openHouseRequests", key), patch);
  return patch;
};

// Only the requests nobody has acted on yet, for a date that has not passed: the
// sidebar count listens to this one. Needs the openHouseRequests composite index
// (status ASC, date ASC) in firestore.indexes.json.
export const subscribeNewOpenHouseRequests = (callback) => {
  const q = query(
    collection(firestored, "openHouseRequests"),
    where("status", "==", "requested"),
    where("date", ">=", todayIso()),
  );
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

// Send to Seller: stamp each request with who asked the sellers, when, whom, and the
// per-request decision token the email buttons carry. One transaction, so a
// two-request email never leaves one request sent and the other not, and so the write
// is conditional on what the request is NOW:
//   - a request that no longer exists is skipped (a batch.update on it would reject
//     the whole commit and fail Send to Seller for every request in the email);
//   - a request already answered (approved or declined, or carrying an openHouseId) is
//     left alone. Resend, or a second Send to Seller click, must never move a request
//     backwards: the seller's fresh Approve link would create a second open house.
// `tokens[i]` (generateFeedbackToken() in the caller) is required for every request:
// a request marked sent without one shows "Sent to Seller" while every button in the
// seller's inbox is a dead link. `dates[i]` is the request's proposed date (the token
// dies the day after it); when the caller does not pass it, the request's own date
// is used. Returns { count, skipped } so the caller can say "1 request was already
// answered".
export const markRequestsSentToSeller = async (
  requestIds,
  { userId, userName, recipients, tokens, dates } = {},
) => {
  assertCanWrite();
  const ids = (requestIds || []).map((id) => String(id || "")).filter(Boolean);
  if (!ids.length) return { count: 0, skipped: [] };
  if (
    !Array.isArray(tokens) ||
    tokens.length !== ids.length ||
    tokens.some((t) => !String(t || "").trim())
  ) {
    throw new Error("markRequestsSentToSeller: one decision token per request is required");
  }
  const refs = ids.map((id) => doc(firestored, "openHouseRequests", id));
  return runTransaction(firestored, async (tx) => {
    // Every read before any write (a Firestore transaction rule).
    const snaps = await Promise.all(refs.map((ref) => tx.get(ref)));
    const now = Date.now();
    const skipped = [];
    let count = 0;
    snaps.forEach((snap, i) => {
      if (!snap.exists()) {
        skipped.push(ids[i]);
        return;
      }
      const d = snap.data() || {};
      if (d.status === "approved" || d.status === "declined" || d.openHouseId) {
        skipped.push(ids[i]);
        return;
      }
      tx.update(refs[i], {
        status: "sent_to_seller",
        sellerAsk: {
          sentAt: now,
          sentBy: userId || "",
          sentByName: userName || "",
          recipients: recipients || [],
          decisionToken: String(tokens[i]).trim(),
          decisionTokenExpiresAt: decisionTokenExpiresAt(dates?.[i] || d.date || ""),
        },
        updatedAt: now,
      });
      count += 1;
    });
    return { count, skipped };
  });
};

// The Open Houses switch on a listing (deal.openHousePolicy). Dotted paths only, so the
// auto-saving Listing Details form and every other deal screen can never wipe it, and
// only the keys present in `patch` are written. Returns false when there was nothing
// to write.
export const setOpenHousePolicy = async (dealId, patch, user) => {
  assertCanWrite();
  const p = patch || {};
  const update = {};
  if (Object.prototype.hasOwnProperty.call(p, "allowed")) {
    let allowed = null;
    if (p.allowed === true) allowed = true;
    else if (p.allowed === false) allowed = false;
    update["openHousePolicy.allowed"] = allowed;
  }
  if (Object.prototype.hasOwnProperty.call(p, "sellerApproval")) {
    update["openHousePolicy.sellerApproval"] =
      p.sellerApproval === "preapproved" ? "preapproved" : "each";
  }
  // Who may request it on the hosting page (spec 09/18/2026 decision 2): "anyone" (and, since
  // 10/05/2026, "listingAgent") only when asked for exactly; anything else is Our Agents Only. Same
  // rule as policyOf.
  if (Object.prototype.hasOwnProperty.call(p, "audience")) {
    update["openHousePolicy.audience"] =
      p.audience === "anyone" || p.audience === "listingAgent" ? p.audience : "team";
  }
  if (Object.prototype.hasOwnProperty.call(p, "hostNotes")) {
    update["openHousePolicy.hostNotes"] = String(p.hostNotes || "");
  }
  if (!Object.keys(update).length) return false;
  update["openHousePolicy.updatedAt"] = Date.now();
  update["openHousePolicy.updatedBy"] = user?.id || "";
  update["openHousePolicy.updatedByName"] =
    [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.displayname || "";
  const dealRef = doc(firestored, "deals", String(dealId));
  retireLaunchRead("deals");
  await updateDoc(dealRef, update);
  return true;
};

// ---- The public hosting link (appSettings/openHouseHosting) ----
// One team-wide token the public hosting page carries (/#/host?t=...). The page never
// reads this doc; GET /oh-host/listings compares the token server-side.

export const getOpenHouseHostingLink = async () => {
  const snap = await getDoc(doc(firestored, "appSettings", "openHouseHosting"));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return { token: String(data.token || ""), enabled: data.enabled === true };
};

// 16 random bytes as 32 hex characters.
function randomHexToken() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Regenerating kills every copy of the old link (agents holding it see "This link is
// no longer active"). createdAt is kept from the first generation.
export const regenerateOpenHouseHostingLink = async (userId) => {
  assertCanWrite();
  const ref = doc(firestored, "appSettings", "openHouseHosting");
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() || {} : {};
  const now = Date.now();
  const token = randomHexToken();
  await setDoc(
    ref,
    {
      token,
      enabled: true,
      createdAt: Number(existing.createdAt) || now,
      updatedAt: now,
      updatedBy: userId || "",
    },
    { merge: true },
  );
  return { token };
};

export const setOpenHouseHostingEnabled = async (enabled, userId) => {
  assertCanWrite();
  await setDoc(
    doc(firestored, "appSettings", "openHouseHosting"),
    { enabled: enabled === true, updatedAt: Date.now(), updatedBy: userId || "" },
    { merge: true },
  );
};

// "Mark Entered" on the Requests tab: the open house was typed into NorthstarMLS
// Add/Edit by hand. Dotted paths so the rest of the `mls` map survives.
export const markOpenHouseMlsEntered = async (openHouseId, user) => {
  assertCanWrite();
  await updateDoc(doc(firestored, "openHouses", String(openHouseId)), {
    "mls.status": "entered",
    "mls.enteredAt": Date.now(),
    "mls.enteredBy": user?.id || "",
    "mls.enteredByName":
      [user?.firstname, user?.lastname].filter(Boolean).join(" ") || user?.displayname || "",
    updatedAt: serverTimestamp(),
    updatedBy: user?.id || "",
  });
};

// The kiosk's live watch on ONE open house (Henry, 09/16/2026). An iPad already
// taking sign-ins has to close itself within a second or two of Mark Complete,
// with nobody touching it, and come back the same way on Undo or Reopen. Polling
// would force a choice of how long to keep asking, and every answer is wrong; a
// listener has no give-up point.
// Same shape as subscribeMyRcConnection (utils/texting.js:170): one document, the
// unsubscribe function handed straight back to the caller, and an error callback.
// The listener never speaks for the network, because on a kiosk that would turn
// a working screen into "This open house isn't available." whenever the wifi
// hiccuped:
// - Being offline is not an error to Firestore. A tablet that opens the page
//   with no signal gets "not found" from its own memory (this app keeps no
//   stored copy between loads). That means "not known yet", so it is ignored;
//   only a "not found" the server has confirmed is passed on as null. The
//   kiosk's loader and its retry already handle a page opened offline.
// - A failed listen (permission-denied, say) is logged, never passed on as
//   null. Firestore does not restart a watch after an error, so a caller that
//   needs to start a new one passes onError (review of 09/16/2026).
export const subscribeOpenHouse = (id, callback, onError) => {
  if (!id) return () => {};
  return onSnapshot(
    doc(firestored, "openHouses", id),
    (snap) => {
      if (!snap.exists() && snap.metadata?.fromCache) return;
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    },
    (err) => {
      console.error("subscribeOpenHouse failed:", err);
      if (onError) onError(err);
    },
  );
};

// ---- Open House "Up For Grabs" opportunities ----

export const createOpportunity = async ({
  deal,
  suggestedTiming,
  approverLcId,
  userId,
  userName,
}) => {
  assertCanWrite();
  const ref = doc(collection(firestored, "openHouseOpportunities"));
  const payload = {
    id: ref.id,
    dealId: deal.id,
    propertyAddress: deal.propertyaddress || "",
    listingAgentId: deal.agentData || "",
    listingAgentName: deal.agentName || "",
    postedBy: userId,
    postedByName: userName || "",
    postedAt: serverTimestamp(),
    suggestedTiming: suggestedTiming || "",
    status: "Open",
    approverLcId: approverLcId || "",
    requests: [],
    declineNote: "",
    resultingOpenHouseId: null,
    cancelledBy: null,
    cancelledAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, payload);
  return { id: ref.id };
};

// Share an Up For Grabs opportunity to the team feed and remember the post id
// on the opportunity (mirrors the feedPostId-on-lead pattern), so the post
// can be rewritten when the slot is filled or cancelled. Feed problems never
// block the opportunity itself.
export const postUpForGrabsToFeed = async (opportunityId, opp, user = {}) => {
  assertCanWrite();
  let post;
  try {
    post = await createPost({
      id: generatePostId(),
      authorId: user.id,
      authorName:
        [user.firstname, user.lastname].filter(Boolean).join(" ") || user.name || "",
      authorRole: user.realRole || user.role || "",
      text: buildUpForGrabsPostText(opp),
    });
    await updateDoc(doc(firestored, "openHouseOpportunities", opportunityId), {
      feedPostId: post.id,
    });
    // An Up For Grabs post is a post like any other: the team hears about it (10/05/2026).
    alertTeamAboutPostInBackground(post.id);
    return { success: true, post };
  } catch (error) {
    console.error("Error sharing Up For Grabs to feed:", error);
    // If the post went live but the opportunity couldn't remember its id, the
    // post would never receive filled/cancelled updates — take it back down.
    if (post?.id) {
      try {
        await deletePost(post.id);
      } catch (cleanupError) {
        console.error("Orphaned Up For Grabs post cleanup failed:", cleanupError);
      }
    }
    return { success: false, error: error.message };
  }
};

// Rewrite the linked feed post (filled/cancelled). Missing post id or a
// deleted post is fine — the feed must never break fill/cancel.
//
// Known gap (pre-existing, 09/24/2026): this writes to Firestore only. The copy
// of the post already sitting in context is not updated, so until the feed is
// re-read, the card on screen still shows the old wording, and the Feed search
// bar, which reads that same context array, therefore matches the old wording
// too. That is not a search bug; do not chase it there. The fix is to dispatch
// UPDATE_POST from the claim and cancel paths, which means threading a dispatch
// into these helpers, and belongs to the Up For Grabs feature, not to search.
const updateUpForGrabsFeedPost = async (feedPostId, text) => {
  if (!feedPostId) return;
  try {
    await updatePost(feedPostId, { text });
  } catch (error) {
    console.error("Up For Grabs feed post update failed:", error);
  }
};

// Every slot ever posted, filled and cancelled ones included. The Open Houses page
// shows all of them, so it uses this one.
export const subscribeOpportunities = (callback) => {
  const q = query(collection(firestored, "openHouseOpportunities"));
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

// Only the slots still Open. The Agent's Home card shows nothing else, so it no
// longer downloads every slot ever posted (2026-09-14, speed wave 3).
export const subscribeOpenOpportunities = (callback) => {
  const q = query(
    collection(firestored, "openHouseOpportunities"),
    where("status", "==", "Open"),
  );
  return onSnapshot(q, (snap) =>
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  );
};

// Add/replace the requesting agent's pending request. Transactional so two
// agents requesting at once don't clobber each other's entry.
export const addOpportunityRequest = async (opportunityId, request) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const data = snap.data();
    if (data.status !== "Open") throw new Error("This slot is no longer open.");
    const requests = upsertRequest(data.requests || [], request);
    tx.update(ref, { requests, updatedAt: serverTimestamp() });
  });
};

// Refuse a claim whose window overlaps an open house this home already has.
//
// Henry, 09/25/2026: "I don't want things ever overlapping." Up For Grabs was the one door with no
// check on it at all: whatever window the winning agent proposed became a real open house, even on
// a home that already had one in that slot. His definition of an overlap has not changed, in his
// own words: "An open house can overlap in the sense that if somebody is already doing it 1 to 3,
// somebody can do it 3 to 5." So back to back passes and only a genuine overlap is stopped, judged
// by the same findClashes the Schedule Open House dialog uses.
//
// This runs BEFORE the claim transaction on purpose. That transaction marks the slot Filled, and
// the shared feed post is rewritten the instant it commits, so a check made any later would leave
// a slot advertised as taken with no open house behind it. Refusing here leaves the slot Open, the
// feed post as it was and every request still Pending: nothing to undo.
//
// The narrow race this does NOT close, said plainly: two people approving two different slots on
// the same home in the same second can both pass this check, because a Firestore transaction
// guards the one opportunity document, never the openHouses collection. The Schedule dialog is
// exposed the same way. Closing it properly needs the check on the server, not in the browser.
async function refuseOverlappingClaim({ oppRef, winnerRequestId, deal }) {
  const snap = await getDoc(oppRef);
  if (!snap.exists()) return;
  const opp = snap.data() || {};
  const winner = (opp.requests || []).find((r) => r.id === winnerRequestId);
  // No such request, or one already answered: the transaction below is the authority on that and
  // says so in its own words. Do not answer for it here.
  if (!winner) return;

  let existing;
  try {
    const rows = await getDocs(
      query(collection(firestored, "openHouses"), where("dealId", "==", deal?.id)),
    );
    existing = rows.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("approveOpportunityRequest: could not read this home's open houses:", err);
    // An approval we cannot check is exactly the one Henry does not want going through, so a
    // failed read refuses rather than guesses.
    throw new Error(
      "Could not check this home's other open houses, so nothing was approved. Try again.",
    );
  }

  const clashes = findClashes(existing, {
    dealId: deal?.id,
    date: winner.proposedDate,
    startTime: winner.proposedStartTime,
    endTime: winner.proposedEndTime,
  });
  if (!clashes.length) return;
  // The street line only: the toast that carries this is read at a glance.
  const home =
    String(deal?.propertyaddress || opp.propertyAddress || "").split(",")[0].trim() || "This home";
  throw new Error(
    `${home} already has an open house on ${clashWhen(clashes[0])}, which this request overlaps. ` +
      "Open houses on one home can touch end to end, but they can never overlap, so nothing was approved.",
  );
}

// Approve a request: create the real open house (host = winner, time = proposed),
// mark the opportunity Filled, stamp the winner Approved + the rest Declined.
// Returns { openHouseId, occupied } so the caller can fire scheduling emails for
// vacant homes (occupied homes hold for seller approval, same as ScheduleModal).
export const approveOpportunityRequest = async ({
  opportunityId,
  winnerRequestId,
  deal,
  userId,
}) => {
  assertCanWrite();
  const oppRef = doc(firestored, "openHouseOpportunities", opportunityId);
  const occupied =
    (deal?.occupied || "").toString().trim().toLowerCase() === "yes";

  // First, before anything is written: does this home already have an open house at the time this
  // agent asked for? A yes throws, and nothing below runs (Henry, 09/25/2026).
  await refuseOverlappingClaim({ oppRef, winnerRequestId, deal });

  // Atomically claim the opportunity for this winner BEFORE creating the open
  // house, so two simultaneous approvals can't both produce one. The loser's
  // transaction sees status !== "Open" (or the request no longer Pending) and
  // throws. We capture `winner` from the committed run to build the OH after.
  let winner;
  let oppData;
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(oppRef);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const opp = snap.data();
    oppData = opp;
    if (opp.status !== "Open") throw new Error("This slot is no longer open.");
    winner = (opp.requests || []).find((r) => r.id === winnerRequestId);
    if (!winner) throw new Error("Request not found");
    if (winner.status !== "Pending")
      throw new Error("That request is no longer pending.");
    tx.update(oppRef, {
      status: "Filled",
      requests: applyWinner(opp.requests || [], winnerRequestId),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });

  // The slot is committed as Filled from here on, so rewrite the shared feed
  // post NOW — even if creating the open house below fails, the feed must
  // not keep advertising a claimed slot.
  await updateUpForGrabsFeedPost(
    oppData?.feedPostId,
    buildUpForGrabsFilledText(oppData, winner),
  );

  // Claim won — create the real open house and link it back to the opportunity.
  const created = await createOpenHouse({
    deal,
    fields: {
      hostingAgentId: winner.agentId,
      hostingAgentName: winner.agentName,
      hostingAgentEmail: winner.agentEmail || "",
      date: winner.proposedDate,
      startTime: winner.proposedStartTime,
      endTime: winner.proposedEndTime,
      notes: winner.note || "",
      pendingSellerApproval: occupied,
    },
    userId,
  });

  await updateDoc(oppRef, {
    resultingOpenHouseId: created.id,
    updatedAt: serverTimestamp(),
  });

  return { openHouseId: created.id, occupied };
};

export const declineAllOpportunityRequests = async (opportunityId, note, userId) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Opportunity not found");
    const data = snap.data();
    tx.update(ref, {
      requests: declineAll(data.requests || []),
      declineNote: note || "",
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  });
};

export const cancelOpportunity = async (opportunityId, userId) => {
  assertCanWrite();
  const ref = doc(firestored, "openHouseOpportunities", opportunityId);
  let cancelledData;
  await runTransaction(firestored, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    cancelledData = data;
    tx.update(ref, {
      status: "Cancelled",
      requests: declineAll(data.requests || []),
      cancelledBy: userId,
      cancelledAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  // Keep the shared feed post truthful now that the slot is gone.
  if (cancelledData) {
    await updateUpForGrabsFeedPost(
      cancelledData.feedPostId,
      buildUpForGrabsCancelledText(cancelledData),
    );
  }
};

// Sent-email history for one address — the lead drawer's Conversation view.
// One-shot read; emailrecepts docs carry {id: ms timestamp, to, subject,
// body, type, createdby}.
export const getEmailsToAddress = async (email) => {
  const raw = String(email || "").trim();
  if (!raw) return [];
  // Firestore equality is case-sensitive and stored `to` values are as
  // typed — try the address as given and lowercased, dedupe.
  const addrs = [...new Set([raw, raw.toLowerCase()])];
  try {
    const seen = new Map();
    for (const a of addrs) {
      const snap = await getDocs(
        query(collection(firestored, "emailrecepts"), where("to", "==", a)),
      );
      snap.docs.forEach((d) => seen.set(d.id, { ...d.data(), docId: d.id }));
    }
    return [...seen.values()];
  } catch (error) {
    console.error("Error loading email history:", error);
    return [];
  }
};

export const getEligibleListingsForOpenHouse = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// ── GUESSED CLOSING DATES ────────────────────────────────────────────────
// Deals whose closing date the app invented (see utils/closingDateAudit).
// Read-only: this only reports, so the list can be eyeballed before anything
// changes. A genuine same-day closing is indistinguishable from a guess, which
// is exactly why clearing is a separate, explicit second step.
export const auditGuessedClosingDates = async () => {
  const snap = await getDocs(collection(firestored, "deals"));
  const deals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return {
    scanned: deals.length,
    suspects: auditDealsForGuessedClosingDates(deals),
  };
};

// Blanks the closing date on the given deal ids — and ONLY those ids, which
// come from an audit the human has just reviewed. Each doc is re-checked
// before it's touched, so a deal edited since the audit is skipped rather
// than clobbered.
export const clearGuessedClosingDates = async (ids) => {
  assertCanWrite();
  const list = Array.isArray(ids) ? ids : [];
  let cleared = 0;
  let skipped = 0;
  for (const id of list) {
    const ref = doc(firestored, "deals", String(id));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      skipped++;
      continue;
    }
    if (!looksLikeGuessedClosingDate({ id: snap.id, ...snap.data() })) {
      skipped++;
      continue;
    }
    retireLaunchRead("deals");
    await updateDoc(ref, { closingdate: "" });
    cleared++;
  }
  return { cleared, skipped };
};

// One-time: stamp listingAgentId onto existing openHouses docs from their deal,
// so the "listings I own" visibility filter works for historical open houses.
// Safe to re-run — skips docs that already have it.
export const backfillOpenHouseListingAgent = async () => {
  assertCanWrite();
  const snap = await getDocs(collection(firestored, "openHouses"));
  let updated = 0;
  for (const d of snap.docs) {
    const oh = d.data() || {};
    if (oh.listingAgentId || !oh.dealId) continue;
    const dealSnap = await getDoc(doc(firestored, "deals", String(oh.dealId)));
    if (!dealSnap.exists()) continue;
    const deal = dealSnap.data() || {};
    if (!deal.agentData) continue;
    await updateDoc(d.ref, {
      listingAgentId: deal.agentData,
      listingAgentName: deal.agentName || "",
    });
    updated++;
  }
  return { updated };
};

// ---- Open House Check-In ---------------------------------------------------
const CHECKINS_COLLECTION = "openHouseCheckins";
const CHECKIN_TEMPLATES_COLLECTION = "openHouseQuestionTemplates";

// Lead lookups for the deal's Lead History panel (deal.sourceLeadId).
export const getBuyerLeadById = async (id) => {
  try {
    const snap = await getDoc(doc(firestored, BUYER_LEADS_COLLECTION, String(id)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error loading buyer lead:", error);
    return null;
  }
};

export const getListingLeadById = async (id) => {
  try {
    const snap = await getDoc(doc(firestored, LISTING_LEADS_COLLECTION, String(id)));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error loading listing lead:", error);
    return null;
  }
};

export const getOpenHouseById = async (id) => {
  const snap = await getDoc(doc(firestored, "openHouses", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Quick Check-In: an open house created on the spot from just an address
// (fields built by adHocOpenHouseFields), so agents can run the kiosk for
// open houses that were never put in the scheduler. Mirrors createOpenHouse's
// bookkeeping fields so the list/recap flows treat it like any other row.
export const createAdHocOpenHouse = async (fields, userId) => {
  assertCanWrite();
  try {
    const ref = doc(collection(firestored, "openHouses"));
    await setDoc(ref, {
      ...fields,
      id: ref.id,
      // No listing behind a Quick Check-In, so no lockboxes (the list and the three old fields),
      // and no Seller Provided Door Code either.
      lockboxes: [],
      lockboxType: "",
      lockboxNumber: "",
      lockboxCode: "",
      sellerDoorCode: "",
      pendingSellerApproval: false,
      feedbackToken: generateFeedbackToken(),
      feedback: null,
      pendingRecap: false,
      recapSentAt: null,
      recapSentBy: null,
      cancelReason: null,
      cancelledAt: null,
      cancelledBy: null,
      createdAt: serverTimestamp(),
      createdBy: userId || "",
      updatedAt: serverTimestamp(),
    });
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error creating quick open house:", error);
    return { success: false, error: error.message };
  }
};

// One completed kiosk sign-in. Batched with the counter bumps on the
// openHouses doc so totals can never drift from the sign-in docs.
export const saveOpenHouseCheckin = async (data) => {
  assertCanWrite();
  try {
    const ref = doc(collection(firestored, CHECKINS_COLLECTION));
    const batch = writeBatch(firestored);
    batch.set(ref, {
      ...data,
      id: ref.id,
      sortedAs: null,
      sortedAt: 0,
      sortedBy: "",
      leadId: "",
      contactId: data.contactId || "",
      createdAt: Date.now(),
    });
    batch.update(doc(firestored, "openHouses", data.openHouseId), {
      checkinPeople: increment(Number(data.partySize) || 1),
      checkinGroups: increment(1),
      checkinCount: increment(1),
    });
    await batch.commit();
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error saving open house check-in:", error);
    // `code` lets the kiosk tell a refused write ("permission-denied": the
    // iPad's login was switched off) apart from a dead connection, and say so
    // on screen instead of queueing the guest forever (punch list line 38).
    return { success: false, error: error.message, code: error.code || "" };
  }
};

export const listenOpenHouseCheckins = (openHouseId, callback) => {
  const q = query(
    collection(firestored, CHECKINS_COLLECTION),
    where("openHouseId", "==", openHouseId),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    callback(rows);
  });
};

// The +1 buttons (and their undo) on the traffic counter. Deltas may be
// negative; deriveTraffic clamps displayed totals at zero.
export const bumpOpenHouseTraffic = async (id, { people = 0, groups = 0 }) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, "openHouses", id), {
      ...(people ? { manualPeople: increment(people) } : {}),
      ...(groups ? { manualGroups: increment(groups) } : {}),
    });
    return { success: true };
  } catch (error) {
    console.error("Error bumping open house traffic:", error);
    return { success: false, error: error.message };
  }
};

export const markCheckinSorted = async (id, patch) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, CHECKINS_COLLECTION, id), {
      ...patch,
      sortedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error marking check-in sorted:", error);
    return { success: false, error: error.message };
  }
};

// Atomically claim a check-in for the MANUAL Sort & Send flow so it can never
// race the automatic openHouseCheckins trigger into two leads for one guest.
// Both paths gate on the same field (sortedAs): whoever writes it first wins,
// the other backs off. Returns { claimed } — when false, the auto trigger (or
// another sorter) already handled this guest and the caller must skip it.
export const claimCheckinForSort = async (id, sortAs, sortedBy) => {
  assertCanWrite();
  try {
    return await runTransaction(firestored, async (tx) => {
      const ref = doc(firestored, CHECKINS_COLLECTION, id);
      const snap = await tx.get(ref);
      if (snap.exists() && snap.data()?.sortedAs) return { claimed: false };
      tx.update(ref, { sortedAs: sortAs, sortedBy, sortedAt: Date.now() });
      return { claimed: true };
    });
  } catch (error) {
    console.error("Error claiming check-in:", error);
    return { claimed: false, error: error.message };
  }
};

// ---- Per-agent question templates ----
export const saveCheckinTemplate = async (data) => {
  assertCanWrite();
  try {
    const ref = doc(collection(firestored, CHECKIN_TEMPLATES_COLLECTION));
    await setDoc(ref, {
      ...data,
      id: ref.id,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      lastUsedAt: 0,
    });
    return { success: true, id: ref.id };
  } catch (error) {
    console.error("Error saving check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const updateCheckinTemplate = async (id, patch) => {
  assertCanWrite();
  try {
    await updateDoc(doc(firestored, CHECKIN_TEMPLATES_COLLECTION, id), {
      ...patch,
      updatedAt: Date.now(),
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const deleteCheckinTemplate = async (id) => {
  assertCanWrite();
  try {
    await deleteDoc(doc(firestored, CHECKIN_TEMPLATES_COLLECTION, id));
    return { success: true };
  } catch (error) {
    console.error("Error deleting check-in template:", error);
    return { success: false, error: error.message };
  }
};

export const listenCheckinTemplates = (ownerId, callback) => {
  const q = query(
    collection(firestored, CHECKIN_TEMPLATES_COLLECTION),
    where("ownerId", "==", ownerId),
  );
  return onSnapshot(q, (snap) => {
    const rows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    callback(rows);
  });
};

// ---- Buyer lead activity: edit / delete ----------------------------------
// A logged activity is one entry in the lead's `activities` array. Appends go
// through arrayUnion, but an edit or delete has to rewrite the array, so both
// run in a transaction: read the current list, rewrite it, write it back. A
// concurrent append lands either before (and is kept) or after (transaction
// retries) — nothing logged in the meantime is lost. Matching is by the
// entry's id (or its at/type/note for the few pre-id entries).
export const updateBuyerActivity = async (leadId, target, patch) => {
  assertCanWrite();
  try {
    await runTransaction(firestored, async (tx) => {
      const ref = doc(firestored, BUYER_LEADS_COLLECTION, String(leadId));
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Lead not found");
      const now = Date.now();
      const current = snap.data()?.activities || [];
      const activities = replaceActivity(current, target, patch, now);
      if (activities.every((a, i) => a === current[i])) {
        throw new Error("That activity is no longer on the timeline");
      }
      tx.update(ref, { activities, updatedAt: now });
    });
    return { success: true };
  } catch (error) {
    console.error("Error updating buyer activity:", error);
    return { success: false, error: error.message };
  }
};

export const deleteBuyerActivity = async (leadId, target) => {
  assertCanWrite();
  try {
    await runTransaction(firestored, async (tx) => {
      const ref = doc(firestored, BUYER_LEADS_COLLECTION, String(leadId));
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error("Lead not found");
      const data = snap.data() || {};
      const remaining = removeActivity(data.activities || [], target);
      if (remaining.length === (data.activities || []).length) {
        throw new Error("That activity is no longer on the timeline");
      }
      const patch = { activities: remaining, updatedAt: Date.now() };
      // Only the entry that stamped lastContacted may move it (see helper).
      const lastContacted = lastContactedAfterDelete(data, target, remaining);
      if (lastContacted !== null) patch.lastContacted = lastContacted;
      tx.update(ref, patch);
    });
    return { success: true };
  } catch (error) {
    console.error("Error deleting buyer activity:", error);
    return { success: false, error: error.message };
  }
};

// ---- Leads that came from open houses ------------------------------------
// Every lead minted from a kiosk sign-in (manual Sort & Send or the server
// trigger) carries `openHouseId`, on both pipelines. One listener returns them
// all, tagged with which pipeline they live in, so the Open Houses page can
// show "3 Leads" per row and list them without loading every lead in the CRM.
// `!=` only returns docs that HAVE the field, which is exactly the set we want.
export const listenOpenHouseLeads = (callback, onError) => {
  let buyers = [];
  let sellers = [];
  const emit = () =>
    callback([...buyers, ...sellers].filter((l) => !(Number(l.deletedAt) > 0)));
  // A failed query must not read as "No Leads" everywhere — tell the page.
  const fail = (side) => (err) => {
    console.error(`listenOpenHouseLeads (${side}) failed:`, err);
    if (onError) onError(err);
  };
  const unsubBuyers = onSnapshot(
    query(collection(firestored, BUYER_LEADS_COLLECTION), where("openHouseId", "!=", "")),
    (snap) => {
      buyers = snap.docs.map((d) => ({ id: d.id, ...d.data(), leadType: "buyer" }));
      emit();
    },
    fail("buyers"),
  );
  const unsubSellers = onSnapshot(
    query(collection(firestored, LISTING_LEADS_COLLECTION), where("openHouseId", "!=", "")),
    (snap) => {
      sellers = snap.docs.map((d) => ({ id: d.id, ...d.data(), leadType: "seller" }));
      emit();
    },
    fail("sellers"),
  );
  return () => {
    unsubBuyers();
    unsubSellers();
  };
};

// Mass "Mark Complete" (no feedback) for a set of open houses — same write
// updateOpenHouse makes for one row, batched. Chunked well under Firestore's
// 500-writes-per-batch ceiling. Returns { success, count }.
export const markOpenHousesComplete = async (ids, userId) => {
  assertCanWrite();
  const list = Array.from(new Set((ids || []).map(String).filter(Boolean)));
  try {
    for (let i = 0; i < list.length; i += 400) {
      const batch = writeBatch(firestored);
      list.slice(i, i + 400).forEach((id) => {
        batch.update(doc(firestored, "openHouses", id), {
          status: "Completed",
          updatedAt: serverTimestamp(),
          updatedBy: userId || "",
        });
      });
      await batch.commit();
    }
    return { success: true, count: list.length };
  } catch (error) {
    console.error("Error marking open houses complete:", error);
    return { success: false, count: 0, error: error.message };
  }
};
